<script setup lang="ts">
import { nextTick, onMounted } from 'vue';
import { useUserStore } from '~/store/userStore';
import citys from "@/assets/data/citys.json";
import sigungus from "@/assets/data/sigungus.json";
import dongs from "@/assets/data/dongs.json";
import { useTocStore } from '~/store/tocStore';
// @ts-ignore
import * as d3 from 'd3';


const props = defineProps<{
  apiData: any
}>()

const emit = defineEmits<{
  regionSelected: [data: { regionCode: string, regionName: string, regionEng: string, zlocId: string, zlocType: string }],
  apiParamsChanged: [params: { parentDongCode?: string | null | undefined, groupLevel?: string | null | undefined }]
}>()

const userStore = useUserStore();
const tocStore = useTocStore();

const userInfo = computed(() => {
  return userStore.loginUserInfo;
})

// 지도 데이터 및 상태
const zloc = ref<any>(null);
const isLoading = ref(true);
const loadError = ref<string | null>(null);

// 지도 선택 상태 관리
const selectedRegion = ref<string | null>(null);
const isMapSelected = ref(false);

// 호버된 지역 상태 (카드 강조용)
const hoveredRegion = ref<string>('');

// 호버 상태 변경 감지
watch(hoveredRegion, () => {
  forceUpdate.value++;
}, { immediate: true });

// 사용자 정보 변경 감지 및 초기 지역 로드
watch(userInfo, async (newUserInfo) => {
  if (newUserInfo && newUserInfo.area && newUserInfo.areaKind) {
    console.log('👤 사용자 정보 변경 감지:', newUserInfo);
    await loadInitialRegion();
  }
}, { immediate: true, deep: true });

// 컴포넌트 마운트 시 초기 지역 로드
onMounted(async () => {
  console.log('🚀 컴포넌트 마운트 - 초기 지역 로드 시작');
  await loadInitialRegion();
});

// 강제 업데이트를 위한 ref
const forceUpdate = ref(0);

// API 파라미터 상태 관리
const currentApiParams = ref<{
  parentDongCode?: string | null | undefined;
  groupLevel?: string | null | undefined;
}>({
  parentDongCode: null,
  groupLevel: null
});

// 초기 지역 로드 여부 확인
const isInitialLoadCompleted = ref(false);

// 사용자 액션으로 인한 네비게이션 여부 (초기 로드와 구분)
const isUserNavigation = ref(false);

// 사용자의 최초 로드 단계 추적
const initialLoadLevel = ref<'national' | 'sigungu' | 'dong' | null>(null);

// 사용자 정보를 기반으로 초기 지역 로드하는 함수
const loadInitialRegion = async () => {
  if (isInitialLoadCompleted.value) return;
  
  const user = userInfo.value;
  if (!user || !user.area || !user.areaKind) {
    // console.log('사용자 정보가 없거나 area/areaKind가 없음');
    isInitialLoadCompleted.value = true;
    return;
  }
  
  // console.log('🔍 사용자 정보 기반 초기 지역 로드:', {
  //   area: user.area,
  //   areaKind: user.areaKind
  // });
  
  // area가 00이 아니고 areaKind가 country가 아닌 경우
  if (user.area !== '00' && user.areaKind !== 'country') {
    const areaCode = user.area;
    const areaKind = user.areaKind;
    
    if (areaKind === 'city') {
      // 시도 레벨: area가 4300000000 형태 (10자리)
      const cityCode = areaCode.substring(0, 2); // 앞 2자리
      console.log(`🏙️ 시도 레벨 로드: ${cityCode}`);
      
      // 최초 로드 단계 설정
      initialLoadLevel.value = 'sigungu';
      
      // selectedRegionCode 설정 (시도 코드)
      selectedRegionCode.value = cityCode;
      
      // 해당 시도의 시군구 로드
      await loadSubRegions(cityCode, getCityNameByCode(cityCode));
      
      // API 파라미터 업데이트
      updateApiParameters(cityCode, 'sigungu');
      
    } else if (areaKind === 'sigungu') {
      // 시군구 레벨: area가 4375000000 형태 (10자리)
      const cityCode = areaCode.substring(0, 2); // 앞 2자리
      const sigunguCode = areaCode.substring(0, 5); // 앞 5자리
      console.log(`🏘️ 시군구 레벨 로드: ${cityCode} -> ${sigunguCode}`);
      
      // 최초 로드 단계 설정
      initialLoadLevel.value = 'dong';
      
      // selectedRegionCode 설정 (시도 코드)
      selectedRegionCode.value = cityCode;
      
      // 먼저 시도 레벨 로드
      await loadSubRegions(cityCode, getCityNameByCode(cityCode));
      
      // 그 다음 시군구의 읍면동 로드
      await loadDongs(sigunguCode, getSigunguNameByCode(sigunguCode));
      
      // API 파라미터 업데이트 (5자리 시군구 코드를 10자리로 변환)
      updateApiParameters(sigunguCode, 'dong');
    }
  } else {
    // 전국 레벨 또는 area가 00인 경우
    initialLoadLevel.value = 'national';
  }
  
  isInitialLoadCompleted.value = true;
};

// 시도 코드로 시도명을 가져오는 함수
const getCityNameByCode = (cityCode: string): string => {
  const cityNames: { [key: string]: string } = {
    '11': '서울특별시',
    '26': '부산광역시',
    '27': '대구광역시',
    '28': '인천광역시',
    '29': '광주광역시',
    '30': '대전광역시',
    '31': '울산광역시',
    '36': '세종특별자치시',
    '41': '경기도',
    '43': '충청북도',
    '44': '충청남도',
    '46': '전라남도',
    '47': '경상북도',
    '48': '경상남도',
    '50': '제주특별자치도',
    '51': '강원특별자치도',
    '52': '전북특별자치도'
  };
  return cityNames[cityCode] || `지역코드 ${cityCode}`;
};

// 시군구 코드로 시군구명을 가져오는 함수
const getSigunguNameByCode = (sigunguCode: string): string => {
  // sigungus.json에서 해당 코드의 시군구명 찾기
  const sigungu = sigungus.features.find((feature: any) => {
    const bjcd = feature.properties.BJCD;
    return bjcd && bjcd.substring(0, 5) === sigunguCode.substring(0, 5);
  });
  
  return sigungu ? sigungu.properties.NAME : `시군구코드 ${sigunguCode}`;
};

// 시도 코드를 10자리로 변환하는 함수
const convertCityCodeTo10Digits = (cityCode: string): string => {
  // 시도 코드(2자리)를 10자리로 변환 (뒤에 8자리 0 추가)
  return cityCode.padEnd(10, '0');
};

// 시군구 코드를 10자리로 변환하는 함수
const convertSigunguCodeTo10Digits = (sigunguCode: string): string => {
  // 시군구 코드(5자리)를 10자리로 변환 (뒤에 5자리 0 추가)
  return sigunguCode.padEnd(10, '0');
};

// API 호출 상태 모니터링
const apiCallTimeout = ref<NodeJS.Timeout | null>(null);
const lastApiCallTime = ref<number>(0);

// API 호출 상태 모니터링 함수
const monitorApiCall = () => {
  const now = Date.now();
  lastApiCallTime.value = now;
  
  // 5초 후에도 API 호출이 완료되지 않으면 재시도
  if (apiCallTimeout.value) {
    clearTimeout(apiCallTimeout.value);
  }
  
  apiCallTimeout.value = setTimeout(() => {
    // 마지막 API 파라미터로 재호출
    if (currentApiParams.value) {
      emit('apiParamsChanged', currentApiParams.value);
    }
  }, 5000);
};

// API 파라미터 업데이트 함수
const updateApiParameters = (parentDongCode: string | null, groupLevel: string | null) => {
  // parentDongCode를 10자리로 변환
  let convertedParentDongCode = parentDongCode;
  if (parentDongCode) {
    if (parentDongCode.length === 2) {
      // 시도 코드(2자리)를 10자리로 변환
      convertedParentDongCode = convertCityCodeTo10Digits(parentDongCode);
    } else if (parentDongCode.length === 5) {
      // 시군구 코드(5자리)를 10자리로 변환
      convertedParentDongCode = convertSigunguCodeTo10Digits(parentDongCode);
    }
    // 이미 10자리인 경우 그대로 사용
  }
  
  const newParams = {
    parentDongCode: convertedParentDongCode,
    groupLevel
  };
  
  currentApiParams.value = newParams;
  
  // API 호출 모니터링 시작
  monitorApiCall();
  
  // BaseComponent에 파라미터 전달을 위해 이벤트 발생
  emit('apiParamsChanged', newParams);
  
  // API 호출 보장을 위한 추가 emit (nextTick 사용)
  nextTick(() => {
    emit('apiParamsChanged', newParams);
  });
};

// 경계상자 표시 여부
const showBoundingBoxes = ref<boolean>(false);

// 지도 확대/축소 상태 관리 (지도 영역만 변환)
const mapScale = ref<number>(1);
const mapTranslateX = ref<number>(0);
const mapTranslateY = ref<number>(0);
const isDragging = ref<boolean>(false);
const lastMousePos = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const mapTransform = ref<string>('translate(0,0) scale(1)');

// 가상 스크롤링을 위한 상태
const leftPanelScrollTop = ref<number>(0);
const rightPanelScrollTop = ref<number>(0);
const visibleCardCount = ref<number>(8); // 화면에 보이는 카드 수

// 키보드 단축키로 경계상자 토글
const toggleBoundingBoxes = () => {
  showBoundingBoxes.value = !showBoundingBoxes.value;
};

// 지도 확대/축소 함수들
const updateMapTransform = () => {
  mapTransform.value = `translate(${mapTranslateX.value}, ${mapTranslateY.value}) scale(${mapScale.value})`;
};


// 마우스 휠 이벤트 처리 (마우스 위치 기준 확대/축소)
const handleWheel = (event: WheelEvent) => {
  event.preventDefault();
  
  // 시군구 지도에서는 휠 이벤트를 제한적으로 허용
  if (isShowingSubRegions.value) {
    // 시군구 지도에서는 휠 이벤트 비활성화 (크기가 이미 최적화됨)
    return;
  }
  
  const delta = event.deltaY > 0 ? 0.9 : 1.1;
  const newScale = mapScale.value * delta;
  
  // 스케일 제한
  if (newScale >= 0.2 && newScale <= 5) {
    // 마우스 위치를 기준으로 확대/축소
    const rect = (event.currentTarget as SVGElement).getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // 마우스 위치를 SVG 좌표계로 변환
    const svgX = (mouseX / rect.width) * 1000;
    const svgY = (mouseY / rect.height) * 900;
    
    // 확대/축소 중심점 계산
    const scaleRatio = newScale / mapScale.value;
    const newTranslateX = svgX - (svgX - mapTranslateX.value) * scaleRatio;
    const newTranslateY = svgY - (svgY - mapTranslateY.value) * scaleRatio;
    
    mapScale.value = newScale;
    mapTranslateX.value = newTranslateX;
    mapTranslateY.value = newTranslateY;
    updateMapTransform();
  }
};

// 마우스 드래그 이벤트 처리 (개선된 버전)
const handleMouseDown = (event: MouseEvent) => {
  // 시군구 지도에서는 드래그를 비활성화하고 클릭만 허용
  if (isShowingSubRegions.value) {
    // 시군구 지도에서는 드래그 비활성화
    return;
  }
  
  // 지도 영역에서만 드래그 시작 (전국 지도에서만)
  if (event.target instanceof SVGPathElement || event.target instanceof SVGGElement) {
    isDragging.value = true;
    lastMousePos.value = { x: event.clientX, y: event.clientY };
    event.preventDefault();
    event.stopPropagation();
  }
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value) return;
  
  const deltaX = event.clientX - lastMousePos.value.x;
  const deltaY = event.clientY - lastMousePos.value.y;
  
  mapTranslateX.value += deltaX;
  mapTranslateY.value += deltaY;
  updateMapTransform();
  
  lastMousePos.value = { x: event.clientX, y: event.clientY };
  event.preventDefault();
};

const handleMouseUp = (event: MouseEvent) => {
  if (isDragging.value) {
    isDragging.value = false;
    event.preventDefault();
  }
};

// 터치 이벤트 처리 (모바일 지원)
const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length === 1) {
    // 지도 영역에서만 터치 시작
    if (event.target instanceof SVGPathElement || event.target instanceof SVGGElement) {
      isDragging.value = true;
      lastMousePos.value = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value || event.touches.length !== 1) return;
  
  const deltaX = event.touches[0].clientX - lastMousePos.value.x;
  const deltaY = event.touches[0].clientY - lastMousePos.value.y;
  
  mapTranslateX.value += deltaX;
  mapTranslateY.value += deltaY;
  updateMapTransform();
  
  lastMousePos.value = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  event.preventDefault();
};

const handleTouchEnd = (event: TouchEvent) => {
  if (isDragging.value) {
    isDragging.value = false;
    event.preventDefault();
  }
};

// 하위 지역(시군구) 로드 함수 (개선된 버전)
const loadSubRegions = async (regionCode: string, regionName: string) => {
  try {
    // 먼저 API 데이터가 있는지 확인
    const apiData = findApiDataByCityCode(regionCode, false);
    if (!apiData) {
      subRegionData.value = [];
      isShowingSubRegions.value = false;
      selectedRegionCode.value = '';
      return;
    }
    
    // 시도 코드 매핑 테이블 (일부 지역 코드가 다를 수 있음)
    const regionCodeMapping: { [key: string]: string[] } = {
      '11': ['11'], // 서울
      '26': ['26'], // 부산
      '27': ['27'], // 대구
      '28': ['28'], // 인천
      '29': ['29'], // 광주
      '30': ['30'], // 대전
      '31': ['31'], // 울산
      '36': ['36'], // 세종
      '41': ['41'], // 경기도
      '43': ['43'], // 충청북도
      '44': ['44'], // 충청남도
      '46': ['46'], // 전라남도
      '47': ['47'], // 경상북도
      '48': ['48'], // 경상남도
      '50': ['50'], // 제주
      '51': ['51'], // 강원
      '52': ['52']  // 전북
    };
    
    // sigungus.json에서 해당 지역의 시군구들 찾기 (개선된 검색)
    const searchCodes = regionCodeMapping[regionCode] || [regionCode];
    const subRegions = sigungus.features.filter((feature: any) => {
      const bjcd = feature.properties.BJCD;
      if (!bjcd) return false;
      
      // 여러 방법으로 매칭 시도
      const bjcdPrefix = bjcd.substring(0, 2);
      const matches = searchCodes.includes(bjcdPrefix);
      
      return matches;
    });
    
    
    if (subRegions.length === 0) {
      // 시군구 데이터 초기화
      subRegionData.value = [];
      isShowingSubRegions.value = false;
      selectedRegionCode.value = '';
      
      return;
    }
    
    // 시군구 데이터를 처리 - BJCD 앞 4자리가 동일한 그룹만 특별 처리
    const processedSubRegions = new Map();
    
    // 먼저 모든 시군구를 그룹화 (MultiPolygon 분리 포함)
    subRegions.forEach((feature: any) => {
        const bjcd = feature.properties.BJCD;
      const name = feature.properties.NAME;
      const prefix4 = bjcd.substring(0, 4);
      const fifthDigit = bjcd.charAt(4);
      
      // 영동군과 증평군 예외 처리: 별도 그룹으로 분리
      let groupKey = prefix4;
      if (bjcd.startsWith('4374000000')) {
        groupKey = '4374_yeongdong'; // 영동군 전용 그룹
      } else if (bjcd.startsWith('4374500000')) {
        groupKey = '4374_jeungpyeong'; // 증평군 전용 그룹
      }
      
      if (!processedSubRegions.has(groupKey)) {
        processedSubRegions.set(groupKey, {
          parentName: null,
          subRegions: [],
          hasMultipleSubRegions: false
        });
      }
      
      // 5번째 자리가 0인 경우 - 부모 지역명으로 저장
      if (fifthDigit === '0') {
        processedSubRegions.get(groupKey).parentName = name;
      }
      
      // 모든 시군구를 subRegions에 추가 (5번째 자리가 0인 것도 포함)
      // MultiPolygon인 경우 지도 표시용으로 분리하되, 데이터 카드에서는 하나로 인식
      if (feature.geometry.type === 'MultiPolygon') {
        // MultiPolygon의 각 부분을 지도 표시용으로 분리하여 추가
        feature.geometry.coordinates.forEach((polygonCoords: any, index: number) => {
          const polygonGeometry = {
            type: 'Polygon',
            coordinates: polygonCoords
          };
          
          // 지도 표시용으로는 각각의 부분을 별도로 추가 (모든 지역이 표시되도록)
          processedSubRegions.get(groupKey).subRegions.push({
            regionCode: `${bjcd}_${index + 1}`, // 지도 표시용 고유 코드
            regionName: name, // 데이터 카드에서는 같은 이름 사용
            regionEng: name,
            fifthDigit: fifthDigit,
            isMultiPolygonPart: true, // MultiPolygon의 일부임을 표시
            originalBjcd: bjcd, // 원본 BJCD 저장
            partIndex: index + 1, // 부분 인덱스
            zloc: {
              zlocId: `sigungu_${bjcd}_${index + 1}`,
              zlocType: 'sigungu',
              zlocGeo: polygonGeometry
            }
          });
        });
      } else {
        // 일반 Polygon인 경우 그대로 추가
        processedSubRegions.get(groupKey).subRegions.push({
          regionCode: bjcd,
          regionName: name,
          regionEng: name,
          fifthDigit: fifthDigit, // 5번째 자리 정보 추가
          isMultiPolygonPart: false, // 일반 Polygon임을 표시
          originalBjcd: bjcd,
          partIndex: 1,
          zloc: {
            zlocId: `sigungu_${bjcd}`,
            zlocType: 'sigungu',
            zlocGeo: feature.geometry
          }
        });
      }
    });
    
    // 각 그룹에서 하위 지역이 2개 이상인지 확인
    processedSubRegions.forEach((group, groupKey) => {
      group.hasMultipleSubRegions = group.subRegions.length > 1;
    });
    
    // 최종 결과 생성 - API 데이터가 있는 시도에 속한 시군구만 처리
    const subRegionResults: any[] = [];
    
    processedSubRegions.forEach((group, groupKey) => {
      group.subRegions.forEach((subRegion: any) => {
        // 영동군과 증평군 예외 처리: 이 두 지역은 서로 다른 지역으로 처리
        const isYeongdong = subRegion.regionCode.startsWith('4374000000');
        const isJeungpyeong = subRegion.regionCode.startsWith('4374500000');
        
        if (isYeongdong || isJeungpyeong) {
          // console.log(`${subRegion.regionName} (${subRegion.regionCode}): 영동군/증평군 예외 처리로 경계를 그림`);
          // 예외 처리: 영동군과 증평군은 항상 경계를 그림
        } else if (subRegion.fifthDigit === '0') {
          // 5번째 자리가 '0'인 시군구의 경우, 같은 BJCD prefix4 내에서 5번째 자리가 '0'이 아닌 시군구가 있는지 확인
          const hasNonZeroSubRegions = group.subRegions.some((otherSubRegion: any) => 
            otherSubRegion.fifthDigit !== '0'
          );
          
          if (hasNonZeroSubRegions) {
            // console.log(`${subRegion.regionName} (${subRegion.regionCode}): 같은 BJCD prefix4 내에 5번째 자리가 '0'이 아닌 시군구가 있어서 경계를 그리지 않음`);
            return; // 경계를 그리지 않음
          }
          // 같은 BJCD prefix4 내에 5번째 자리가 '0'이 아닌 시군구가 없으면 경계를 그림 (단독 시군구)
          // console.log(`${subRegion.regionName} (${subRegion.regionCode}): 단독 시군구로 경계를 그림`);
        }
        
        // 화면 출력 시 _숫자 접미사 제거
        let finalName = subRegion.regionName.replace(/_\d+$/, '');
        
        // BJCD 앞 4자리가 동일한 경우 5번째 자리가 '0'인 시군구 이름을 앞에 붙이기
        // 단, BJCD가 완벽하게 동일한 경우는 제외
        if (group.parentName && subRegion.fifthDigit !== '0') {
          // 5번째 자리가 '0'이 아닌 시군구에만 부모 이름을 앞에 붙이기
          finalName = `${group.parentName} ${finalName}`;
        }
        
        const result = {
          regionCode: subRegion.regionCode,
          regionName: finalName,
          regionEng: finalName,
          zloc: subRegion.zloc
        };
        
        subRegionResults.push(result);
      });
    });
    
    subRegionData.value = subRegionResults;
    isShowingSubRegions.value = true;
    // selectedRegionCode.value는 이미 handleRegionClick에서 설정됨
    
    // 시군구 데이터 로딩 완료 후 상태 확인
    // console.log(`시군구 데이터 로딩 완료 상태 확인:`);
    // console.log(`- subRegionData.length: ${subRegionData.value.length}`);
    // console.log(`- isShowingSubRegions: ${isShowingSubRegions.value}`);
    // console.log(`- selectedRegionCode: ${selectedRegionCode.value}`);
    // console.log(`- nationalMapSvgPaths.length: ${nationalMapSvgPaths.value.length}`);
    
    console.log(`${regionName} 시군구 로드 완료: ${subRegionResults.length}개 (해당 시도의 모든 시군구)`);
    
    // 시군구별 API 데이터 매핑 확인
    // console.log('🔍 시군구별 API 데이터 매핑 확인:');
    subRegionResults.forEach((sigungu, index) => {
      const cityCode = convertSigunguToCityCode(sigungu.regionCode);
      const apiData = findApiDataByCityCode(cityCode, true);
      console.log(`📍 [${index + 1}] ${sigungu.regionName} (${sigungu.regionCode}) → 시도 ${cityCode}:`, {
        hasApiData: !!apiData,
        apiData: apiData ? {
          cityCode: apiData.cityCode,
          cityNameAlias: apiData.cityNameAlias,
          todayProdKWH: apiData.todayProdKWH
        } : null
      });
    });
    
    // 전북특별자치도 최종 결과 디버깅 (제거)
    // if (regionCode === '52') {
    //   console.log('전북특별자치도 최종 결과:');
    //   subRegionResults.forEach((result: any) => {
    //     console.log(`  최종 표시: ${result.regionName} (${result.regionCode})`);
    //   });
    // }
    
    // 그룹별 처리 결과 요약 (제거)
    // console.log('그룹별 처리 결과:');
    // processedSubRegions.forEach((group, groupKey) => {
    //   console.log(`  ${groupKey}: 하위지역 ${group.subRegions.length}개, 부모지역: ${group.parentName || '없음'}, 다중지역: ${group.hasMultipleSubRegions}`);
    // });
    
    // 제외된 시군구 (5번째 자리가 '0'이면서 BJCD 앞 4자리가 동일한 다른 시군구가 있는 경우) 목록
    const excludedRegions = subRegions.filter((feature: any) => {
      const bjcd = feature.properties.BJCD;
      const prefix4 = bjcd.substring(0, 4);
      const fifthDigit = bjcd.charAt(4);
      
      // 영동군과 증평군은 예외 처리로 제외하지 않음
      if (bjcd.startsWith('4374000000') || bjcd.startsWith('4374500000')) {
        return false;
      }
      
      if (fifthDigit === '0') {
        const group = processedSubRegions.get(prefix4);
        return group && group.subRegions.length > 0;
      }
      return false;
    });
    
    // if (excludedRegions.length > 0) {
    //   console.log('경계를 그리지 않는 시군구 (5번째 자리가 0이면서 하위 시군구가 있는 경우):', excludedRegions.map((f: any) => `${f.properties.NAME} (${f.properties.BJCD})`));
    // }
    
    // 단독 시군구 (5번째 자리가 '0'이지만 하위 시군구가 없는 경우) 목록
    const standaloneRegions = subRegions.filter((feature: any) => {
      const bjcd = feature.properties.BJCD;
      const prefix4 = bjcd.substring(0, 4);
      const fifthDigit = bjcd.charAt(4);
      
      // 영동군과 증평군은 예외 처리로 단독 시군구로 처리
      if (bjcd.startsWith('4374000000') || bjcd.startsWith('4374500000')) {
        return true;
      }
      
      if (fifthDigit === '0') {
        const group = processedSubRegions.get(prefix4);
        return !group || group.subRegions.length === 0;
      }
      return false;
    });
    
    if (standaloneRegions.length > 0) {
      console.log('단독 시군구 (5번째 자리가 0이지만 하위 시군구가 없어서 경계를 그림):', standaloneRegions.map((f: any) => `${f.properties.NAME} (${f.properties.BJCD})`));
    }
    
  } catch (error) {
    console.error(`${regionName} 시군구 로드 실패:`, error);
  }
};

// 히스토리에 항목 추가
const addToHistory = (level: 'city' | 'district' | 'neighborhood', regionCode: string, regionName: string) => {
  navigationHistory.value.push({
    level,
    regionCode,
    regionName,
    timestamp: Date.now()
  });
  // console.log('히스토리 추가:', { level, regionCode, regionName });
};

// 히스토리에서 마지막 항목 제거
const removeLastFromHistory = () => {
  const removed = navigationHistory.value.pop();
  // console.log('히스토리에서 제거:', removed);
  return removed;
};

// 전체 지도로 돌아가기 (시군구에서 시도로 돌아가기)
const backToNationalMap = () => {
  // console.log('🔄 시군구에서 시도로 돌아가기 시작');
  // console.log('현재 API 파라미터:', currentApiParams.value);
  // console.log('현재 API 데이터:', props.apiData);
  
  // 모든 상태 완전 초기화
  isShowingSubRegions.value = false;
  isShowingDongs.value = false;
  selectedRegionCode.value = null; // 시도 코드 완전 초기화
  selectedSigunguCode.value = null; // 시군구 코드 완전 초기화
  selectedRegion.value = null; // 선택된 지역 초기화
  isMapSelected.value = false; // 지도 선택 상태 초기화
  hoveredRegion.value = ''; // 호버 상태 초기화
  subRegionData.value = []; // 시군구 데이터 초기화
  dongData.value = [];
  
  // 사용자 네비게이션 상태 리셋 (뒤로가기는 사용자 액션이 아님)
  isUserNavigation.value = false;
  
  // 히스토리 초기화
  navigationHistory.value = [];
  
  
  // API 파라미터 초기화 (시도 데이터 요청을 위해 undefined 사용)
  
  // 시도 데이터 요청을 위해 undefined 값으로 설정
  const cityParams = {
    parentDongCode: undefined,
    groupLevel: undefined
  };
  
  // 강제로 변경을 감지하도록 임시 값을 설정한 후 undefined로 변경
  currentApiParams.value = { parentDongCode: 'temp-reset', groupLevel: 'temp-reset' };
  
  nextTick(() => {
    currentApiParams.value = cityParams;
    emit('apiParamsChanged', cityParams);
    
    // 여러 번 emit하여 확실하게 API 호출 보장
    const emitApiCall = (attempt: number) => {
      setTimeout(() => {
        emit('apiParamsChanged', cityParams);
        
        // 최대 3번까지 재시도
        if (attempt < 3) {
          emitApiCall(attempt + 1);
        }
      }, 100 * attempt); // 100ms, 200ms, 300ms 간격으로 재시도
    };
    
    emitApiCall(1);
  });
};

// 읍면동에서 시군구로 돌아가기
const backToSigunguMap = () => {
  isShowingDongs.value = false; // 읍면동 지도 숨김
  // isShowingSubRegions는 이미 true이므로 그대로 유지
  selectedSigunguCode.value = null; // 시군구 코드 완전 초기화
  dongData.value = []; // 읍면동 데이터만 초기화, 시군구 데이터는 유지
  
  // 사용자 네비게이션 상태 리셋 (뒤로가기는 사용자 액션이 아님)
  isUserNavigation.value = false;
  
  // 히스토리에서 마지막 항목 제거 (읍면동 레벨)
  removeLastFromHistory();
  
  // 시군구 레벨로 API 파라미터 업데이트 (개선된 로직)
  if (selectedRegionCode.value) {
    updateApiParameters(selectedRegionCode.value, 'sigungu');
    
    // API 호출 보장을 위한 추가 emit
    nextTick(() => {
      const sigunguParams = {
        parentDongCode: selectedRegionCode.value,
        groupLevel: 'sigungu'
      };
      emit('apiParamsChanged', sigunguParams);
    });
  }
};

// 하위 지역(읍면동) 로드 함수
const loadDongs = async (sigunguCode: string, sigunguName: string) => {
  try {
    // 먼저 해당 시도에 API 데이터가 있는지 확인
    const cityCode = sigunguCode.substring(0, 2); // 시군구 코드에서 시도 코드 추출
    const apiData = findApiDataByCityCode(cityCode, true);
    if (!apiData) {
      dongData.value = [];
      isShowingDongs.value = false;
      selectedSigunguCode.value = '';
      return;
    }
    
    
    // dongs.json에서 해당 시군구의 읍면동들 찾기 (혼재된 구조 처리)
    const dongRegions = (dongs as any).features.filter((feature: any) => {
      // 새로운 구조 (BJCD, NAME) 또는 기존 구조 (EMD_CD, EMD_NM) 처리
      const bjcd = feature.properties.BJCD || feature.properties.EMD_CD;
      const name = feature.properties.NAME || feature.properties.EMD_NM;
      
      if (!bjcd) return false;
      
      // 시군구 코드와 매칭 (BJCD/EMD_CD 앞 5자리)
      const dongBjcdPrefix = bjcd.substring(0, 5);
      const matches = dongBjcdPrefix === sigunguCode;
      
      return matches;
    });
    
    
    if (dongRegions.length === 0) {
      return;
    }
    
    // 읍면동 데이터 처리
    const dongResults: any[] = [];
    
    dongRegions.forEach((feature: any) => {
      // 새로운 구조 (BJCD, NAME) 또는 기존 구조 (EMD_CD, EMD_NM) 처리
      const bjcd = feature.properties.BJCD || feature.properties.EMD_CD;
      const name = feature.properties.NAME || feature.properties.EMD_NM;
      
      // API 데이터에서 해당 읍면동이 있는지 확인
      // 1. 정확한 매칭 시도
      let hasApiData = props.apiData?.some((item: any) => item.dongCode === bjcd);
      
      if (!hasApiData) {
        // 2. BJCD 앞 8자리로 매칭 시도 (마지막 자리수 차이 처리)
        hasApiData = props.apiData?.some((item: any) => {
          const apiCode = item.dongCode;
          if (!apiCode) return false;
          // 앞 8자리가 같은지 확인 (마지막 2자리는 무시)
          return apiCode.substring(0, 8) === bjcd.substring(0, 8);
        }) || false;
        
        if (hasApiData) {
          console.log(`읍면동 처리: ${name} (${bjcd}) - API 데이터 존재 (앞 8자리 매칭): ${hasApiData}`);
        } else {
          
          // 매칭되지 않는 경우 유사한 코드 찾기
          const similarApiCodes = props.apiData?.filter((item: any) => {
            const apiCode = item.dongCode;
            if (!apiCode) return false;
            // 앞 8자리가 같은지 확인
            return apiCode.substring(0, 8) === bjcd.substring(0, 8);
          }) || [];
          
          if (similarApiCodes.length > 0) {
            console.log(`  유사한 API 코드 발견:`, similarApiCodes.map((item: any) => ({
              dongCode: item.dongCode,
              dongNameAlias: item.dongNameAlias
            })));
          }
        }
      } else {
      }
      
      dongResults.push({
        regionCode: bjcd,
        regionName: name,
        regionEng: name,
        zloc: {
          zlocId: `dong_${bjcd}`,
          zlocType: 'dong',
          zlocGeo: feature.geometry
        }
      });
    });
    
    // API 데이터가 있는 시도에 속한 읍면동만 필터링
    const filteredDongResults = dongResults.filter(dong => {
      const cityCode = convertDongToCityCode(dong.regionCode);
      const apiData = findApiDataByCityCode(cityCode, false);
      const hasApiData = !!apiData;
      
      if (!hasApiData) {
        console.log(`🗑️ ${dong.regionName} (${dong.regionCode}): API 데이터 없음 - 읍면동에서 제외`);
      }
      
      return hasApiData;
    });
    
    
    dongData.value = filteredDongResults;
    isShowingDongs.value = true;
    selectedSigunguCode.value = sigunguCode;
    // selectedRegionCode.value는 이미 handleSigunguClick에서 설정됨
    
    console.log(`${sigunguName} 읍면동 로드 완료: ${filteredDongResults.length}개 (API 데이터 있는 읍면동만)`);
    console.log('로드된 읍면동 목록:', filteredDongResults.map(r => `${r.regionName} (${r.regionCode})`));
    
  } catch (error) {
    console.error(`${sigunguName} 읍면동 로드 실패:`, error);
  }
};



// 전국 지역 코드 목록 (checkRegionCodeToEng 함수 참고)
const nationalRegionCodes = [
  { code: "11", name: "서울특별시", eng: "seoul" },
  { code: "26", name: "부산광역시", eng: "busan" },
  { code: "27", name: "대구광역시", eng: "daegu" },
  { code: "28", name: "인천광역시", eng: "inchen" },
  { code: "29", name: "광주광역시", eng: "gwangju" },
  { code: "30", name: "대전광역시", eng: "daejeon" },
  { code: "31", name: "울산광역시", eng: "ulsan" },
  { code: "36", name: "세종특별자치시", eng: "sejong" },
  { code: "41", name: "경기도", eng: "keonggi" },
  { code: "43", name: "충청북도", eng: "chungbuk" },
  { code: "44", name: "충청남도", eng: "chungnam" },
  { code: "46", name: "전라남도", eng: "jeonnam" },
  { code: "47", name: "경상북도", eng: "keongbuk" },
  { code: "48", name: "경상남도", eng: "keongnam" },
  { code: "50", name: "제주특별자치도", eng: "jeju" },
  { code: "51", name: "강원특별자치도", eng: "gangwon" },
  { code: "52", name: "전북특별자치도", eng: "jeonbuk" }
];

// citys.json 기반 지도 데이터 저장
const nationalZlocData = ref<Array<{regionCode: string, regionName: string, regionEng: string, zloc: any}>>([]);

// 하위 지역(시군구) 표시 관련 상태
const isShowingSubRegions = ref<boolean>(false);
const selectedRegionCode = ref<string | null>(null);
const subRegionData = ref<Array<{regionCode: string, regionName: string, regionEng: string, zloc: any}>>([]);

// 하위 지역(읍면동) 표시 관련 상태
const isShowingDongs = ref<boolean>(false);
const selectedSigunguCode = ref<string | null>(null);
const dongData = ref<Array<{regionCode: string, regionName: string, regionEng: string, zloc: any}>>([]);

// 뒤로가기 히스토리 관리
const navigationHistory = ref<Array<{
  level: 'city' | 'district' | 'neighborhood',
  regionCode: string,
  regionName: string,
  timestamp: number
}>>([]);

// citys.json 데이터를 사용하여 지도 그리기
onMounted(async () => {
  // 키보드 이벤트 리스너 설정
  const handleKeyPress = (event: KeyboardEvent) => {
    // 'B' 키를 눌렀을 때 경계상자 토글
    if (event.key === 'b' || event.key === 'B') {
      event.preventDefault();
      toggleBoundingBoxes();
    }
    
  };
  
  document.addEventListener('keydown', handleKeyPress);
  
  // 윈도우 리사이즈 이벤트 리스너 설정
  const handleResize = () => {
    // 리사이즈 시 필요한 작업 (현재는 비어있음)
  };
  
  window.addEventListener('resize', handleResize);
  
  // 패널 스크롤 이벤트 리스너 설정
  const handlePanelScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('left-panel')) {
      leftPanelScrollTop.value = target.scrollTop;
    } else if (target.classList.contains('right-panel')) {
      rightPanelScrollTop.value = target.scrollTop;
    }
  };
  
  // 스크롤 이벤트 리스너 추가 (이벤트 위임 사용)
  document.addEventListener('scroll', handlePanelScroll, true);
  
  // 컴포넌트 언마운트 시 이벤트 리스너 제거
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyPress);
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('scroll', handlePanelScroll, true);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    // API 호출 모니터링 타이머 정리
    if (apiCallTimeout.value) {
      clearTimeout(apiCallTimeout.value);
      apiCallTimeout.value = null;
    }
  });

  try {
    // console.log('citys.json 기반 지도 데이터 로드 시작...');
    const startTime = performance.now();
    isLoading.value = true;
    loadError.value = null;
    
    // citys.json에 있는 모든 지역 코드 추출
    const citysRegionCodes = citys.features.map((feature: any) => feature.properties.CTPRVN_CD);
    console.log('citys.json에 있는 모든 지역 코드들:', citysRegionCodes);
    
    // citys.json에 있는 모든 지역을 표시
    const targetRegions = nationalRegionCodes.filter(region => 
      citysRegionCodes.includes(region.code)
    );
    console.log('표시할 모든 지역들:', targetRegions.map(r => `${r.name} (${r.code})`));
    
    // citys.json에서 해당 지역들의 데이터를 찾아서 zloc 형태로 변환
    const mapDataResults = targetRegions.map((region) => {
      try {
        console.log(`${region.name} (${region.code}) citys.json 데이터 처리 중...`);
        
        // citys.json에서 해당 지역 코드에 맞는 데이터 찾기
        const regionData = citys.features.find((feature: any) => feature.properties.CTPRVN_CD === region.code);
        
        if (regionData) {
          console.log(`${region.name} (${region.code}) citys.json 데이터 발견:`, {
            hasGeometry: !!regionData.geometry,
            geometryType: regionData.geometry?.type,
            properties: regionData.properties
          });
          
          // zloc 형태로 변환 (citys.json의 geometry를 직접 사용)
          const zlocData = {
            zlocId: `citys_${region.code}`,
            zlocType: 'city',
            zlocGeo: regionData.geometry || null
          };
          
          return {
            regionCode: region.code,
            regionName: region.name,
            regionEng: region.eng,
            zloc: zlocData
          };
        } else {
          console.warn(`${region.name} (${region.code}) 데이터를 citys.json에서 찾을 수 없음`);
          return {
            regionCode: region.code,
            regionName: region.name,
            regionEng: region.eng,
            zloc: null
          };
        }
      } catch (error) {
        console.error(`${region.name} citys.json 데이터 처리 실패:`, error);
        return {
          regionCode: region.code,
          regionName: region.name,
          regionEng: region.eng,
          zloc: null
        };
      }
    });
    
    // 성공적으로 로드된 데이터만 필터링
    const validResults = mapDataResults.filter(result => result.zloc !== null);
    
    nationalZlocData.value = validResults;
    isLoading.value = false;
    
    const endTime = performance.now();
    const loadTime = endTime - startTime;
    
    // console.log(`citys.json 기반 지도 데이터 로드 완료: ${validResults.length}/${targetRegions.length}개 지역 성공`);
    // console.log(`로딩 시간: ${loadTime.toFixed(2)}ms`);
    // console.log('로드된 지역들:', validResults.map(r => r.regionName));
  
  } catch (error) {
    console.error('citys.json 기반 지도 데이터 로드 실패:', error);
    isLoading.value = false;
    loadError.value = error instanceof Error ? error.message : '알 수 없는 오류';
  }
});

// API 데이터 변경 감지 시 모니터링 중지
watch(() => props.apiData, (newApiData) => {
  if (newApiData && apiCallTimeout.value) {
    // console.log('✅ API 데이터 수신 완료. 모니터링을 중지합니다.');
    clearTimeout(apiCallTimeout.value);
    apiCallTimeout.value = null;
  }
});

// props.apiData 변경 감지하여 zloc 데이터 재로드 (모든 지역 표시)
watch(() => props.apiData, async (newApiData) => {
  // console.log('API 데이터 변경 감지 - 시군구/읍면동 모드에서도 카드 데이터 업데이트');
  try {
    // console.log('API 데이터 변경 감지, 모든 지역 zloc 데이터 재로드 시작...');
    isLoading.value = true;
    loadError.value = null;
    
    // citys.json에 있는 모든 지역 코드 추출
    const citysRegionCodes = citys.features.map((feature: any) => feature.properties.CTPRVN_CD);
    // console.log('citys.json에 있는 모든 지역 코드들:', citysRegionCodes);
    
    // citys.json에 있는 모든 지역을 표시
    const targetRegions = nationalRegionCodes.filter(region => 
      citysRegionCodes.includes(region.code)
    );
    // console.log('재로드할 모든 지역들:', targetRegions.map(r => `${r.name} (${r.code})`));
    
    // citys.json에서 해당 지역들의 데이터를 찾아서 zloc 형태로 변환
    const mapDataResults = targetRegions.map((region) => {
      try {
        // console.log(`${region.name} (${region.code}) citys.json 데이터 처리 중...`);
        
        // citys.json에서 해당 지역 코드에 맞는 데이터 찾기
        const regionData = citys.features.find((feature: any) => feature.properties.CTPRVN_CD === region.code);
        
        if (regionData) {
          // console.log(`${region.name} (${region.code}) citys.json 데이터 발견:`, {
          //   hasGeometry: !!regionData.geometry,
          //   geometryType: regionData.geometry?.type,
          //   properties: regionData.properties
          // });
          
          // zloc 형태로 변환 (citys.json의 geometry를 직접 사용)
          const zlocData = {
            zlocId: `citys_${region.code}`,
            zlocType: 'city',
            zlocGeo: regionData.geometry || null
          };
          
          return {
            regionCode: region.code,
            regionName: region.name,
            regionEng: region.eng,
            zloc: zlocData
          };
        } else {
          console.warn(`${region.name} (${region.code}) 데이터를 citys.json에서 찾을 수 없음`);
          return {
            regionCode: region.code,
            regionName: region.name,
            regionEng: region.eng,
            zloc: null
          };
        }
      } catch (error) {
        console.error(`${region.name} citys.json 데이터 처리 실패:`, error);
        return {
          regionCode: region.code,
          regionName: region.name,
          regionEng: region.eng,
          zloc: null
        };
      }
    });
    
    // 성공적으로 로드된 데이터만 필터링
    const validResults = mapDataResults.filter(result => result.zloc !== null);
    
    nationalZlocData.value = validResults;
    isLoading.value = false;
    
    // console.log(`API 데이터 변경 후 citys.json 데이터 로드 완료: ${validResults.length}/${targetRegions.length}개 지역 성공`);
    // console.log('로드된 지역들:', validResults.map(r => r.regionName));
  
  } catch (error) {
    console.error('API 데이터 변경 후 citys.json 데이터 로드 실패:', error);
    isLoading.value = false;
    loadError.value = error instanceof Error ? error.message : '알 수 없는 오류';
  }
}, { deep: true, immediate: false });

// 시군구/읍면동 모드에서 API 데이터 변경 시 카드 데이터 업데이트
watch([() => props.apiData, isShowingSubRegions, isShowingDongs], (newValues, oldValues) => {
  const [newApiData, newIsShowingSubRegions, newIsShowingDongs] = newValues;
  const [oldApiData, oldIsShowingSubRegions, oldIsShowingDongs] = oldValues || [null, false, false];
  
  if (newIsShowingSubRegions || newIsShowingDongs) {
    // console.log('🔄 시군구/읍면동 모드에서 API 데이터 변경 감지:', {
    //   mode: newIsShowingSubRegions ? '시군구' : '읍면동',
    //   apiDataChanged: newApiData !== oldApiData,
    //   modeChanged: (newIsShowingSubRegions !== oldIsShowingSubRegions) || (newIsShowingDongs !== oldIsShowingDongs),
    //   apiDataLength: newApiData?.length || 0
    // });
    
    // rightPanelData computed가 자동으로 재계산됨
    // console.log('📊 카드 데이터가 자동으로 업데이트됩니다');
  }
}, { deep: true });

// 지역별 지도 클릭 이벤트 핸들러 (고성능 최적화)
const handleRegionClick = (regionData: any) => {
  // 비활성화된 지역이면 클릭 무시
  if (!isRegionActive(regionData.regionCode)) {
    // console.log('비활성화된 지역 클릭 무시:', regionData.regionName);
    return;
  }
  
  // 사용자 액션으로 인한 네비게이션임을 표시
  isUserNavigation.value = true;
  
  // 하위 지역 표시 중이 아닐 때만 하위 지역 로드
  if (!isShowingSubRegions.value) {
    // 시도 선택 시 선택된 지역 상태는 설정하지 않음 (시군구로 바로 이동)
    selectedRegionCode.value = regionData.regionCode; // 시도 코드만 설정
    // console.log(`🖱️ ${regionData.regionName} (${regionData.regionCode}) 클릭 - 시군구로 이동`);
    // 하위 지역이 있는지 확인 (시군구 데이터가 있는지) - 개선된 검색
    const hasSubRegions = sigungus.features.some((feature: any) => {
      const bjcd = feature.properties.BJCD;
      if (!bjcd) return false;
      const bjcdPrefix = bjcd.substring(0, 2);
      return bjcdPrefix === regionData.regionCode;
    });
    
    if (hasSubRegions) {
      // console.log(`🖱️ ${regionData.regionName} 클릭 - 하위 지역 로드 시작`);
      
      // 히스토리에 시도 추가
      addToHistory('city', regionData.regionCode, regionData.regionName);
      
      loadSubRegions(regionData.regionCode, regionData.regionName);
      
      // 시도 선택 시 API 파라미터 업데이트 (시군구 레벨)
      // console.log(`🔄 API 파라미터 업데이트: parentDongCode=${regionData.regionCode}, groupLevel=sigungu`);
      updateApiParameters(regionData.regionCode, 'sigungu');
    } else {
      // console.log(`${regionData.regionName} 클릭 - 하위 지역 없음 (코드: ${regionData.regionCode})`);
      
      // 디버깅: 사용 가능한 시군구 코드들 확인
      // const availableCodes = [...new Set(sigungus.features.map((f: any) => f.properties.BJCD?.substring(0, 2)).filter(Boolean))];
      // console.log('사용 가능한 시군구 지역 코드들:', availableCodes.sort());
    }
  }
  
  // 이벤트 발생
  emit('regionSelected', {
    regionCode: regionData.regionCode,
    regionName: regionData.regionName,
    regionEng: regionData.regionEng,
    zlocId: regionData.zlocId,
    zlocType: regionData.zlocType
  });
};

// 지도 선택 해제 (고성능 최적화)
const clearMapSelection = () => {
  if (!selectedRegion.value) {
    return;
  }
  
  selectedRegion.value = null;
  isMapSelected.value = false;
};

// 지도 배경 클릭 이벤트 핸들러 (지도 이외의 공간 클릭 시 선택 해제)
const handleMapBackgroundClick = (event: MouseEvent) => {
  // 클릭된 요소가 path 요소인지 확인
  const target = event.target as SVGElement;
  
  // path 요소가 아닌 경우에만 선택 해제
  if (target && target.tagName !== 'path') {
    // console.log('지도 배경 클릭: 선택 해제');
    clearMapSelection();
  }
};

// 지도 영역 클릭 이벤트 핸들러 (지도 영역 내 빈 공간 클릭 시 선택 해제)
const handleMapAreaClick = (event: MouseEvent) => {
  // 클릭된 요소가 SVG 요소인지 확인
  const target = event.target as HTMLElement;
  
  // SVG 요소가 아닌 경우 (지도 영역의 빈 공간)에만 선택 해제
  if (target && target.tagName !== 'svg' && target.tagName !== 'path' && target.tagName !== 'g' && target.tagName !== 'text') {
    // console.log('지도 영역 빈 공간 클릭: 선택 해제');
    clearMapSelection();
  }
};

// 시군구 클릭 이벤트 핸들러 (읍면동 로드 포함)
const handleSigunguClick = (event: MouseEvent, regionData: any) => {
  // 이벤트 전파 중지
  event.preventDefault();
  event.stopPropagation();
  
  // 비활성화된 지역이면 클릭 무시
  if (!isRegionActive(regionData.regionCode)) {
    // console.log('비활성화된 시군구 클릭 무시:', regionData.regionName);
    return;
  }
  
  // 사용자 액션으로 인한 네비게이션임을 표시
  isUserNavigation.value = true;
  
  // console.log(`🖱️ 시군구 클릭: ${regionData.regionName} (${regionData.regionCode})`);
  
  // 시군구 선택 시 읍면동 로드
  // 원본 BJCD 사용 (MultiPolygon의 경우 _숫자 접미사 제거)
  const originalBjcd = (regionData as any).originalBjcd || regionData.regionCode.replace(/_\d+$/, '');
  const sigunguCode = originalBjcd.substring(0, 5); // BJCD 앞 5자리
  // console.log(`📍 시군구 원본 BJCD: ${originalBjcd}, 읍면동 매칭용 코드: ${sigunguCode}`);
  
  // 읍면동 모드에서는 시도 코드를 selectedRegionCode로 유지 (시군구의 상위 시도)
  const cityCode = originalBjcd.substring(0, 2); // 시도 코드
  // console.log(`📍 읍면동 모드에서 시도 코드 유지: ${cityCode}`);
  
  // 히스토리에 시군구 추가 (원본 BJCD 사용)
  addToHistory('district', originalBjcd, regionData.regionName);
  
  loadDongs(sigunguCode, regionData.regionName);
  
  // 시군구 선택 시 API 파라미터 업데이트
  // console.log(`🔄 API 파라미터 업데이트: parentDongCode=${sigunguCode}, groupLevel=dong`);
  updateApiParameters(sigunguCode, 'dong');
  
  // 시군구 선택 시 이벤트 발생 (원본 BJCD 사용)
  emit('regionSelected', {
    regionCode: originalBjcd,
    regionName: regionData.regionName,
    regionEng: regionData.regionEng,
    zlocId: `sigungu_${originalBjcd}`,
    zlocType: 'sigungu'
  });
};



// 시군구 마우스 over 이벤트 핸들러 (동일한 이름의 시군구 함께 표시)
const handleSigunguMouseOver = (event: MouseEvent, regionData: any) => {
  // console.log('시군구 지도 마우스 오버:', regionData.regionName, '활성화 상태:', isRegionActive(regionData.regionCode));
  
  if (isRegionActive(regionData.regionCode)) {
    // API 데이터에서 해당 시군구명 찾기
    const originalBjcd = regionData.regionCode.replace(/_\d+$/, '');
    const sigunguCode = originalBjcd.substring(2, 5);
    const apiItem = props.apiData?.find((item: any) => item.sigunguCode === sigunguCode);
    const cardRegionName = apiItem?.sigunguName || regionData.regionName;
    
    // console.log('시군구 모드 - API 데이터에서 찾은 지역명:', cardRegionName);
    hoveredRegion.value = cardRegionName;
    // console.log('호버된 지역 설정:', hoveredRegion.value);
  }
  
  // 동일한 이름을 가진 모든 시군구 찾기
  const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
    path.regionName === regionData.regionName
  );
  
  // 동일한 이름의 시군구들을 모두 하이라이트
  sameNameRegions.forEach((path: any) => {
    const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
    if (pathElement) {
      pathElement.style.fill = '#2E5BBA';
      pathElement.style.stroke = '#1A4A8A';
      pathElement.style.strokeWidth = '3';
      pathElement.style.opacity = '0.95';
    }
  });
};

// 시군구 마우스 out 이벤트 핸들러 (하이라이트 제거)
const handleSigunguMouseOut = (event: MouseEvent, regionData: any) => {
  console.log('시군구 지도 마우스 아웃:', regionData.regionName);
  hoveredRegion.value = '';
  
  // 동일한 이름을 가진 모든 시군구 찾기
  const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
    path.regionName === regionData.regionName
  );
  
  // 동일한 이름의 시군구들의 하이라이트 제거
  sameNameRegions.forEach((path: any) => {
    const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
    if (pathElement) {
      pathElement.style.fill = '#D1E7F5';
      pathElement.style.stroke = '#4A90E2';
      pathElement.style.strokeWidth = '2';
      pathElement.style.opacity = '0.85';
    }
  });
};

// 읍면동 마우스 over 이벤트 핸들러 (같은 지역의 모든 영역 함께 하이라이트)
const handleDongMouseOver = (event: MouseEvent, regionData: any) => {
  console.log('읍면동 지도 마우스 오버:', regionData.regionName, '활성화 상태:', isRegionActive(regionData.regionCode));
  
  if (isRegionActive(regionData.regionCode)) {
    // API 데이터에서 해당 읍면동명 찾기
    const apiItem = props.apiData?.find((item: any) => {
      const apiCode = item.dongCode;
      if (!apiCode) return false;
      // 정확한 매칭 또는 앞 8자리 매칭
      return apiCode === regionData.regionCode || 
             apiCode.substring(0, 8) === regionData.regionCode.substring(0, 8);
    });
    const cardRegionName = apiItem?.dongNameAlias || regionData.regionName;
    
    console.log('읍면동 모드 - API 데이터에서 찾은 지역명:', cardRegionName);
    hoveredRegion.value = cardRegionName;
    console.log('호버된 지역 설정:', hoveredRegion.value);
    
    // 동일한 이름을 가진 모든 읍면동 찾기
    const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
      path.regionName === regionData.regionName
    );
    
    // 동일한 이름의 읍면동들을 모두 하이라이트
    sameNameRegions.forEach((path: any) => {
      const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
      if (pathElement) {
        pathElement.style.fill = '#2E5BBA';
        pathElement.style.stroke = '#1A4A8A';
        pathElement.style.strokeWidth = '3';
        pathElement.style.opacity = '0.95';
      }
    });
  }
};

// 읍면동 마우스 out 이벤트 핸들러 (같은 지역의 모든 영역에서 하이라이트 제거)
const handleDongMouseOut = (event: MouseEvent, regionData: any) => {
  console.log('읍면동 지도 마우스 아웃:', regionData.regionName);
  hoveredRegion.value = '';
  
  // 동일한 이름을 가진 모든 읍면동 찾기
  const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
    path.regionName === regionData.regionName
  );
  
  // 동일한 이름의 읍면동들의 하이라이트 제거
  sameNameRegions.forEach((path: any) => {
    const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
    if (pathElement) {
      pathElement.style.fill = '#D1E7F5';
      pathElement.style.stroke = '#4A90E2';
      pathElement.style.strokeWidth = '2';
      pathElement.style.opacity = '0.85';
    }
  });
};

// 읍면동 클릭 이벤트 핸들러
const handleDongClick = (event: MouseEvent, regionData: any) => {
  console.log('읍면동 클릭:', regionData.regionName);
  // 읍면동 클릭 시 특별한 동작이 필요한 경우 여기에 추가
};


// 동적 투영법 설정 (개선된 버전)
const projection = computed(() => {
  if (isShowingDongs.value && dongData.value.length > 0) {
    // 읍면동 데이터의 실제 좌표 범위를 동적으로 계산
    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    let validRegions = 0;
    
    dongData.value.forEach(region => {
      if (region.zloc?.zlocGeo?.coordinates) {
        const coords = region.zloc.zlocGeo.coordinates;
        
        // MultiPolygon과 Polygon 모두 처리
        const processCoordinates = (coordArray: any) => {
          if (Array.isArray(coordArray[0]) && Array.isArray(coordArray[0][0])) {
            // MultiPolygon의 경우
            coordArray.forEach((polygon: any) => {
              if (Array.isArray(polygon[0]) && typeof polygon[0][0] === 'number') {
                polygon.forEach((coord: number[]) => {
                  const [lon, lat] = coord;
                  minLon = Math.min(minLon, lon);
                  maxLon = Math.max(maxLon, lon);
                  minLat = Math.min(minLat, lat);
                  maxLat = Math.max(maxLat, lat);
                });
              }
            });
          } else if (Array.isArray(coordArray[0]) && typeof coordArray[0][0] === 'number') {
            // Polygon의 경우
            coordArray.forEach((coord: number[]) => {
              const [lon, lat] = coord;
              minLon = Math.min(minLon, lon);
              maxLon = Math.max(maxLon, lon);
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
            });
          }
        };
        
        processCoordinates(coords);
        validRegions++;
      }
    });
    
    // 좌표 범위가 유효한지 확인
    if (validRegions > 0 && minLon !== Infinity && maxLon !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
      const centerLon = (minLon + maxLon) / 2;
      const centerLat = (minLat + maxLat) / 2;
      const lonRange = maxLon - minLon;
      const latRange = maxLat - minLat;
      
      console.log(`읍면동 좌표 범위: 경도 ${minLon.toFixed(4)}~${maxLon.toFixed(4)} (범위: ${lonRange.toFixed(4)}), 위도 ${minLat.toFixed(4)}~${maxLat.toFixed(4)} (범위: ${latRange.toFixed(4)})`);
      console.log(`읍면동 중심: (${centerLon.toFixed(4)}, ${centerLat.toFixed(4)}), 유효 지역: ${validRegions}개`);
      
      // SVG 크기에 맞는 스케일 계산 (SVG viewBox: 1000x900)
      // 화면의 80%를 차지하도록 조정
      const targetWidth = 1000 * 0.8;  // 800px
      const targetHeight = 900 * 0.8; // 720px
      
      // d3.geoMercator 스케일 계산 (올바른 방법)
      // 경도 1도당 픽셀 수 계산
      const pixelsPerDegreeLon = targetWidth / lonRange;
      const pixelsPerDegreeLat = targetHeight / latRange;
      
      // 더 작은 값을 사용하여 비율 유지
      const pixelsPerDegree = Math.min(pixelsPerDegreeLon, pixelsPerDegreeLat);
      
      // d3.geoMercator 스케일은 경도 1도당 픽셀 수와 관련
      // 180도 = π 라디안이므로, 스케일 = 픽셀수 * 180 / π
      const scale = pixelsPerDegree * 180 / Math.PI;
      
      // 최소/최대 스케일 제한 (읍면동에 맞게 조정 - 더 큰 스케일)
      const finalScale = Math.max(10000, Math.min(500000, scale));
      
      console.log(`읍면동 계산된 스케일: ${finalScale.toFixed(0)} (픽셀/도: ${pixelsPerDegree.toFixed(2)}, 경도범위: ${lonRange.toFixed(4)}도, 위도범위: ${latRange.toFixed(4)}도, 목표크기: ${targetWidth}x${targetHeight})`);
      
      return d3.geoMercator()
        .center([centerLon, centerLat])
        .scale(finalScale)
        .translate([500, 450]);
    }
  }
  
  if (isShowingSubRegions.value && subRegionData.value.length > 0) {
    // 시군구 데이터의 실제 좌표 범위를 동적으로 계산
    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    let validRegions = 0;
    
    subRegionData.value.forEach(region => {
      if (region.zloc?.zlocGeo?.coordinates) {
        const coords = region.zloc.zlocGeo.coordinates;
        
        // MultiPolygon과 Polygon 모두 처리
        const processCoordinates = (coordArray: any) => {
          if (Array.isArray(coordArray[0]) && Array.isArray(coordArray[0][0])) {
            // MultiPolygon의 경우
            coordArray.forEach((polygon: any) => {
              if (Array.isArray(polygon[0]) && typeof polygon[0][0] === 'number') {
                polygon.forEach((coord: number[]) => {
            const [lon, lat] = coord;
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
          });
        }
            });
          } else if (Array.isArray(coordArray[0]) && typeof coordArray[0][0] === 'number') {
            // Polygon의 경우
            coordArray.forEach((coord: number[]) => {
              const [lon, lat] = coord;
              minLon = Math.min(minLon, lon);
              maxLon = Math.max(maxLon, lon);
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
            });
          }
        };
        
        processCoordinates(coords);
        validRegions++;
      }
    });
    
    // 좌표 범위가 유효한지 확인
    if (validRegions > 0 && minLon !== Infinity && maxLon !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
      const centerLon = (minLon + maxLon) / 2;
      const centerLat = (minLat + maxLat) / 2;
      const lonRange = maxLon - minLon;
      const latRange = maxLat - minLat;
      
      console.log(`시군구 좌표 범위: 경도 ${minLon.toFixed(4)}~${maxLon.toFixed(4)} (범위: ${lonRange.toFixed(4)}), 위도 ${minLat.toFixed(4)}~${maxLat.toFixed(4)} (범위: ${latRange.toFixed(4)})`);
      console.log(`시군구 중심: (${centerLon.toFixed(4)}, ${centerLat.toFixed(4)}), 유효 지역: ${validRegions}개`);
      
      // SVG 크기에 맞는 스케일 계산 (SVG viewBox: 1000x900)
      // 화면의 80%를 차지하도록 조정
      const targetWidth = 1000 * 0.8;  // 800px
      const targetHeight = 900 * 0.8; // 720px
      
      // d3.geoMercator 스케일 계산 (올바른 방법)
      // 경도 1도당 픽셀 수 계산
      const pixelsPerDegreeLon = targetWidth / lonRange;
      const pixelsPerDegreeLat = targetHeight / latRange;
      
      // 더 작은 값을 사용하여 비율 유지
      const pixelsPerDegree = Math.min(pixelsPerDegreeLon, pixelsPerDegreeLat);
      
      // d3.geoMercator 스케일은 경도 1도당 픽셀 수와 관련
      // 180도 = π 라디안이므로, 스케일 = 픽셀수 * 180 / π
      const scale = pixelsPerDegree * 180 / Math.PI;
      
      // 최소/최대 스케일 제한 (시군구에 맞게 조정)
      const finalScale = Math.max(5000, Math.min(100000, scale));
      
      console.log(`계산된 스케일: ${finalScale.toFixed(0)} (픽셀/도: ${pixelsPerDegree.toFixed(2)}, 경도범위: ${lonRange.toFixed(4)}도, 위도범위: ${latRange.toFixed(4)}도, 목표크기: ${targetWidth}x${targetHeight})`);
      
      return d3.geoMercator()
        .center([centerLon, centerLat])
        .scale(finalScale)
        .translate([500, 450]);
    }
    
    // fallback: 지역별 기본 설정 (개선된 스케일)
    const regionCode = selectedRegionCode.value;
    const regionCenters: { [key: string]: [number, number] } = {
      '11': [126.98, 37.57], // 서울
      '26': [129.08, 35.18], // 부산
      '27': [128.60, 35.87], // 대구
      '28': [126.71, 37.46], // 인천
      '29': [126.85, 35.16], // 광주
      '30': [127.38, 36.35], // 대전
      '31': [129.31, 35.54], // 울산
      '36': [127.29, 36.49], // 세종
      '41': [127.55, 37.26], // 경기도
      '43': [127.49, 36.64], // 충청북도
      '44': [126.85, 36.52], // 충청남도
      '46': [126.89, 34.87], // 전라남도
      '47': [128.89, 36.49], // 경상북도
      '48': [128.24, 35.46], // 경상남도
      '50': [126.53, 33.50], // 제주
      '51': [128.73, 37.82], // 강원
      '52': [127.15, 35.72]  // 전북
    };
    
    // 지역별 스케일 설정 (80% 화면 크기에 맞게 대폭 조정)
    const regionScales: { [key: string]: number } = {
      '11': 80000, // 서울 - 시군구가 많아서 더 확대
      '26': 35000, // 부산
      '27': 35000, // 대구
      '28': 35000, // 인천
      '29': 35000, // 광주
      '30': 35000, // 대전
      '31': 35000, // 울산
      '36': 40000, // 세종
      '41': 25000, // 경기도 - 넓은 지역이므로 적당히
      '43': 20000, // 충청북도
      '44': 20000, // 충청남도
      '46': 20000, // 전라남도
      '47': 20000, // 경상북도
      '48': 20000, // 경상남도
      '50': 30000, // 제주
      '51': 20000, // 강원
      '52': 20000  // 전북
    };
    
    const center = regionCode ? (regionCenters[regionCode] || [126.98, 37.57]) : [126.98, 37.57];
    const scale = regionCode ? (regionScales[regionCode] || 25000) : 25000; // 기본값을 80% 크기에 맞게 대폭 증가
    console.log(`지역별 기본 설정 사용: ${regionCode} - 중심: (${center[0]}, ${center[1]}), 스케일: ${scale}`);
    
    return d3.geoMercator()
      .center(center)
      .scale(scale)
      .translate([500, 450]);
  } else if (isShowingDongs.value) {
    // 읍면동 fallback: 시군구 중심을 기반으로 더 큰 스케일 사용
    const regionCode = selectedSigunguCode.value ? selectedSigunguCode.value.substring(0, 2) : null; // 시군구 코드에서 시도 코드 추출
    const regionCenters: { [key: string]: [number, number] } = {
      '11': [126.98, 37.57], // 서울
      '26': [129.08, 35.18], // 부산
      '27': [128.60, 35.87], // 대구
      '28': [126.71, 37.46], // 인천
      '29': [126.85, 35.16], // 광주
      '30': [127.38, 36.35], // 대전
      '31': [129.31, 35.54], // 울산
      '36': [127.29, 36.49], // 세종
      '41': [127.55, 37.26], // 경기도
      '43': [127.49, 36.64], // 충청북도
      '44': [126.85, 36.52], // 충청남도
      '46': [126.89, 34.87], // 전라남도
      '47': [128.89, 36.49], // 경상북도
      '48': [128.24, 35.46], // 경상남도
      '50': [126.53, 33.50], // 제주
      '51': [128.73, 37.82], // 강원
      '52': [127.15, 35.72]  // 전북
    };
    
    // 읍면동용 스케일 설정 (시군구보다 더 큰 스케일)
    const dongScales: { [key: string]: number } = {
      '11': 150000, // 서울 - 읍면동이 많아서 더 확대
      '26': 80000,  // 부산
      '27': 80000,  // 대구
      '28': 80000,  // 인천
      '29': 80000,  // 광주
      '30': 80000,  // 대전
      '31': 80000,  // 울산
      '36': 100000, // 세종
      '41': 60000,  // 경기도
      '43': 50000,  // 충청북도
      '44': 50000,  // 충청남도
      '46': 50000,  // 전라남도
      '47': 50000,  // 경상북도
      '48': 50000,  // 경상남도
      '50': 80000,  // 제주
      '51': 50000,  // 강원
      '52': 50000   // 전북
    };
    
    const center = regionCode ? (regionCenters[regionCode] || [126.98, 37.57]) : [126.98, 37.57];
    const scale = regionCode ? (dongScales[regionCode] || 80000) : 80000; // 읍면동 기본 스케일
    console.log(`읍면동 fallback 설정 사용: ${regionCode} - 중심: (${center[0]}, ${center[1]}), 스케일: ${scale}`);
    
    return d3.geoMercator()
      .center(center)
      .scale(scale)
      .translate([500, 450]);
  } else {
    // 전체 지도용 투영법
    return d3.geoMercator()
      .center([128.4, 35.85]) // 한국 중심 좌표
      .scale(7000) // 확대/축소 비율
      .translate([600, 450]); // SVG 중심점
  }
});

// d3.js 경로 생성기
const pathGenerator = computed(() => d3.geoPath().projection(projection.value));

// 시군구 중심점 계산 함수
const calculateSigunguCenter = (svgPath: string): { x: number, y: number } => {
  try {
    if (!svgPath || svgPath.length < 10) {
      console.warn('시군구 중심점 계산: SVG path가 너무 짧음');
      return { x: 500, y: 450 }; // 기본값
    }
    
    // SVG path에서 좌표 추출
    const coords = svgPath.match(/-?\d+\.?\d*/g);
    if (!coords || coords.length < 4) {
      console.warn('시군구 중심점 계산: 좌표를 추출할 수 없음');
      return { x: 500, y: 450 }; // 기본값
    }
    
    const coordsNum = coords.map(parseFloat);
    const xCoords = coordsNum.filter((_, i) => i % 2 === 0);
    const yCoords = coordsNum.filter((_, i) => i % 2 === 1);
    
    if (xCoords.length === 0 || yCoords.length === 0) {
      console.warn('시군구 중심점 계산: 유효한 좌표가 없음');
      return { x: 500, y: 450 }; // 기본값
    }
    
    const centerX = xCoords.reduce((sum, x) => sum + x, 0) / xCoords.length;
    const centerY = yCoords.reduce((sum, y) => sum + y, 0) / yCoords.length;
    
    // console.log(`시군구 중심점 계산: (${centerX.toFixed(2)}, ${centerY.toFixed(2)}) - 좌표 수: ${xCoords.length}`);
    
    return { x: centerX, y: centerY };
  } catch (error) {
    console.error('시군구 중심점 계산 실패:', error);
    return { x: 500, y: 450 }; // 기본값
  }
};

// SVG path 정리 함수 (단순화된 버전)
function cleanSvgPath(pathString: string): string | null {
  try {
    if (!pathString || pathString.length < 10) {
      return null;
    }
    
    // SVG path를 파싱하여 첫 번째 부분만 사용
    const parts = pathString.split('Z');
    if (parts.length < 2) {
      return pathString; // Z가 없으면 원본 반환
    }
    
    // 첫 번째 부분만 사용
    const firstPart = parts[0].trim();
    if (firstPart.length < 10) {
      return null;
    }
    
    // 좌표 범위 검증 없이 첫 번째 부분 반환
      return firstPart + 'Z';
  } catch (error) {
    console.error('cleanSvgPath 에러:', error);
    return null;
  }
}

// 간단한 SVG path 변환
const nationalMapSvgPaths = computed(() => {
  let dataSource;
  let dataType;
  
  if (isShowingDongs.value) {
    dataSource = dongData.value;
    dataType = '읍면동';
  } else if (isShowingSubRegions.value) {
    dataSource = subRegionData.value;
    dataType = '시군구';
  } else {
    dataSource = nationalZlocData.value;
    dataType = '시도';
  }
  
  // console.log(`SVG path 변환 시작 - 데이터 소스: ${dataType}, 개수: ${dataSource?.length || 0}`);
  
  if (!dataSource || dataSource.length === 0) {
    console.log('데이터 소스가 없거나 비어있음');
    return [];
  }
  
  // 시군구/읍면동 모드에서는 모든 지역을 표시하되, 활성화된 지역과 비활성화된 지역을 구분
  let filteredDataSource = dataSource;
  if (isShowingDongs.value || isShowingSubRegions.value) {
    // 모든 지역을 표시 (필터링하지 않음)
    console.log(`🔍 ${dataType} 모드: ${dataSource.length}개 지역 모두 지도에 표시 (활성화/비활성화 구분)`);
    console.log(`🔍 활성화된 ${dataType} 지역 코드들:`, activeRegionCodes.value);
  }
  
  const results = filteredDataSource.map((regionData) => {
    try {
      // console.log(`${regionData.regionName} (${regionData.regionCode}) SVG path 생성 시도`);
      
      let geometry;
      
      if (isShowingDongs.value || isShowingSubRegions.value) {
        // 읍면동 또는 시군구 데이터: zloc에서 geometry 추출
        geometry = regionData.zloc?.zlocGeo;
        // console.log(`${regionData.regionName}: ${isShowingDongs.value ? '읍면동' : '시군구'} geometry -`, {
        //   hasZloc: !!regionData.zloc,
        //   hasZlocGeo: !!regionData.zloc?.zlocGeo,
        //   geometryType: regionData.zloc?.zlocGeo?.type,
        //   hasCoordinates: !!regionData.zloc?.zlocGeo?.coordinates,
        //   regionCode: regionData.regionCode,
        //   regionName: regionData.regionName
        // });
        
        // 데이터가 없는 경우 처리
        if (!geometry) {
          // console.log(`${regionData.regionName}: geometry가 없음`);
          return null; // SVG path 생성하지 않음
        }
        
        // geometry 검증
        if (!geometry.coordinates || !Array.isArray(geometry.coordinates)) {
          // console.log(`${regionData.regionName}: 좌표 데이터가 유효하지 않음`);
          return null;
        }
      } else {
        // 시도 데이터: zloc에서 geometry 추출
        if (!regionData.zloc?.zlocGeo) {
          // console.log(`${regionData.regionName}: zlocGeo가 없음`);
        return null;
      }
      
      if (typeof regionData.zloc.zlocGeo === 'string') {
          geometry = JSON.parse(regionData.zloc.zlocGeo);
      } else {
          geometry = regionData.zloc.zlocGeo;
        }
      }
      
      if (!geometry) {
        // console.log(`${regionData.regionName}: geometry가 없음`);
        return null;
      }
      
      // SVG path 생성
      const rawSvgPath = pathGenerator.value(geometry);
      // console.log(`${regionData.regionName}: 원본 SVG path - 길이: ${rawSvgPath?.length || 0}`);
      
      if (rawSvgPath && rawSvgPath.length > 10) {
        if (isShowingSubRegions.value) {
          // 시군구 데이터: 이상한 좌표가 포함된 path 정리
          const cleanedPath = cleanSvgPath(rawSvgPath);
          // console.log(`${regionData.regionName}: 정리된 SVG path - 길이: ${cleanedPath?.length || 0}`);
          
          if (cleanedPath) {
            const result = {
              regionCode: regionData.regionCode,
              regionName: regionData.regionName,
              regionEng: regionData.regionEng,
              svgPath: cleanedPath
            };
            // console.log(`시군구 SVG path 생성 성공: ${regionData.regionName} (${regionData.regionCode}) - path 길이: ${cleanedPath.length}`);
            return result;
          } else {
            console.log(`${regionData.regionName}: 정리된 path가 없음`);
          }
        } else {
          // 시도 데이터: 정리 없이 원본 사용
          console.log(`${regionData.regionName}: 시도 원본 SVG path 사용 - 길이: ${rawSvgPath.length}`);
            return {
              regionCode: regionData.regionCode,
              regionName: regionData.regionName,
              regionEng: regionData.regionEng,
            svgPath: rawSvgPath
            };
        }
          } else {
        console.log(`${regionData.regionName}: 원본 path가 너무 짧거나 없음`);
      }
      
      return null;
    } catch (error) {
      console.error(`${regionData.regionName}: SVG path 생성 실패:`, error);
      return null;
    }
  }).filter(path => path !== null);
  
  // console.log(`SVG path 변환 완료 - 성공: ${results.length}개, 전체: ${dataSource.length}개`);
  return results;
});



// API 데이터 변환
const regionData = computed(() => {
  // console.log('🔍 API 데이터 구조 분석:', {
  //   hasApiData: !!props.apiData,
  //   isArray: Array.isArray(props.apiData),
  //   length: props.apiData?.length,
  //   sampleData: props.apiData?.[0],
  //   currentMode: isShowingSubRegions.value ? '시군구' : (isShowingDongs.value ? '읍면동' : '시도'),
  //   apiParams: currentApiParams.value,
  //   fullApiData: props.apiData // 전체 API 데이터 구조 확인
  // });
  
  if (!props.apiData || !Array.isArray(props.apiData)) {
    // console.log('⚠️ API 데이터가 없거나 배열이 아님');
    return [];
  }

  let result = [];

  for(let i = 0; i < props.apiData.length; i++){
    const item = props.apiData[i];
    
    result.push({
      cityEng: checkRegionCodeToEng(item.cityCode),
      cityCode: item.cityCode,
      cityNameAlias: item.cityNameAlias,
      todayProdKWH: item.todayProdKWH,
      todayUsageKWH: item.todayUsageKWH,
      eqCount: item.eqCount
    })
  }

  // console.log('📊 변환된 시도 데이터:', result.length, '개');
  return result;
})

// 좌측 패널 데이터 (절반)


// 간단한 연결선 계산 (시도 지도에서만 사용)
const dataConnections = ref<Array<{
  regionCode: string;
  regionName: string;
  mapCenter: { x: number; y: number };
  cardPosition: { x: number; y: number };
  isSelected: boolean;
}>>([]);


function checkRegionCodeToEng(cityCode: string){
  switch(cityCode){
    case "11": return 'seoul'
    case "26": return 'busan'
    case "27": return 'daegu'
    case "28": return 'inchen'
    case "29": return 'gwangju'
    case "30": return 'daejeon'
    case "31": return 'ulsan'
    case "36": return 'sejong'
    case "41": return 'keonggi'
    case "43": return 'chungbuk'
    case "44": return 'chungnam'
    case "46": return 'jeonnam'
    case "47": return 'keongbuk'
    case "48": return 'keongnam'
    case "50": return 'jeju'
    case "51": return 'gangwon'
    case "52": return 'jeonbuk'
  }
}

// 간단한 지역 코드 변환 함수

// 시군구/읍면동 코드를 시도 코드로 변환하는 함수
const convertSigunguToCityCode = (sigunguCode: string): string => {
  // MultiPolygon으로 분리된 경우 _숫자 접미사 제거
  const cleanCode = sigunguCode.replace(/_\d+$/, '');
  // 시군구 코드의 앞 2자리를 시도 코드로 사용
  return cleanCode.substring(0, 2);
};

// 읍면동 코드를 시도 코드로 변환하는 함수
const convertDongToCityCode = (dongCode: string): string => {
  // MultiPolygon으로 분리된 경우 _숫자 접미사 제거
  const cleanCode = dongCode.replace(/_\d+$/, '');
  // 읍면동 코드의 앞 2자리를 시도 코드로 사용
  return cleanCode.substring(0, 2);
};

// API 데이터에서 해당 시도 코드의 데이터를 찾는 함수
const findApiDataByCityCode = (cityCode: string, showLog: boolean = false) => {
  // if (showLog) {
  //   console.log(`🔍 API 데이터에서 시도 코드 ${cityCode} 찾는 중...`);
  // }
  
  if (!props.apiData || !Array.isArray(props.apiData)) {
    // if (showLog) {
    //   console.log('⚠️ API 데이터가 없거나 배열이 아님');
    // }
    return null;
  }
  // console.log(props.apiData);

  // if (showLog) {
  //   console.log('📋 사용 가능한 API 데이터:', props.apiData.map(item => ({
  //     cityCode: item.cityCode,
  //     cityNameAlias: item.cityNameAlias,
  //     todayProdKWH: item.todayProdKWH
  //   })));
  // }
  
  const foundData = props.apiData.find(item => item.cityCode === cityCode);
  
  // if (showLog) {
  //   if (foundData) {
  //     console.log(`✅ 시도 코드 ${cityCode} 데이터 발견:`, {
  //       cityNameAlias: foundData.cityNameAlias,
  //       todayProdKWH: foundData.todayProdKWH,
  //       todayUsageKWH: foundData.todayUsageKWH,
  //       eqCount: foundData.eqCount
  //     });
  //   } else {
  //     console.log(`❌ 시도 코드 ${cityCode} 데이터를 찾을 수 없음`);
  //     console.log('🔍 사용 가능한 시도 코드들:', props.apiData.map(item => item.cityCode));
  //   }
  // }
  
  return foundData;
};

// 우측 패널 데이터 (API 응답 데이터를 직접 사용)
const rightPanelData = computed(() => {
  // API 응답 데이터를 직접 사용하여 데이터카드 생성
  if (!props.apiData || !Array.isArray(props.apiData)) {
    return [];
  }
  
  if (isShowingDongs.value && dongData.value.length > 0) {
    // 읍면동 모드: API 응답 데이터를 그대로 데이터카드로 표시 (합치지 않음)
    const apiResults = props.apiData.map(apiItem => {
      return {
        cityEng: apiItem.dongNameAlias, // 읍면동명을 영어명으로 사용
        cityCode: apiItem.dongCode, // 읍면동 코드 사용
        cityNameAlias: apiItem.dongNameAlias, // API 응답의 읍면동명 사용
        todayProdKWH: apiItem.todayProdKWH,
        todayUsageKWH: apiItem.todayUsageKWH,
        eqCount: apiItem.eqCount,
        hasApiData: true,
        isDistributedData: false,
        originalData: apiItem
      };
    });
    
    return apiResults;
    
  } else if (isShowingSubRegions.value && subRegionData.value.length > 0) {
    // 시군구 모드: API 응답 데이터를 그대로 데이터카드로 표시 (합치지 않음)
    const apiResults = props.apiData.map(apiItem => {
      return {
        cityEng: apiItem.sigunguName, // 시군구명을 영어명으로 사용
        cityCode: apiItem.sigunguCode, // 시군구 코드 사용
        cityNameAlias: apiItem.sigunguName, // API 응답의 시군구명 사용
        todayProdKWH: apiItem.todayProdKWH,
        todayUsageKWH: apiItem.todayUsageKWH,
        eqCount: apiItem.eqCount,
        hasApiData: true,
        isDistributedData: false,
        originalData: apiItem
      };
    });
    
    return apiResults;
    
  } else {
    // 시도 모드: API 응답 데이터를 직접 사용
    const apiResults = props.apiData.map(apiItem => {
      return {
        cityEng: checkRegionCodeToEng(apiItem.cityCode),
        cityCode: apiItem.cityCode,
        cityNameAlias: apiItem.cityNameAlias,
        todayProdKWH: apiItem.todayProdKWH,
        todayUsageKWH: apiItem.todayUsageKWH,
        eqCount: apiItem.eqCount,
        hasApiData: true,
        isDistributedData: false,
        originalData: apiItem
      };
    });
    
    return apiResults;
  }
});

// 활성화된 지역 코드 목록
const activeRegionCodes = computed(() => {
  // API 데이터를 기준으로 활성화된 지역 코드 목록 생성
  if (!props.apiData || !Array.isArray(props.apiData)) {
    return [];
  }
  
  let codes: string[] = [];
  
  if (isShowingDongs.value) {
    // 읍면동 모드: API 응답의 읍면동 코드들
    // BJCD 앞 8자리로 매칭하기 위해 dongData의 regionCode 사용
    codes = dongData.value.map(dong => dong.regionCode);
  } else if (isShowingSubRegions.value) {
    // 시군구 모드: API 응답의 시군구 코드들
    codes = props.apiData.map(item => item.sigunguCode);
  } else {
    // 시도 모드: API 응답의 시도 코드들
    codes = props.apiData.map(item => item.cityCode);
  }
  
  return codes;
});

// 지역이 활성화되어 있는지 확인하는 함수
const isRegionActive = (regionCode: string) => {
  // MultiPolygon의 경우 원본 BJCD로 변환하여 확인
  const originalBjcd = regionCode.replace(/_\d+$/, '');
  
  if (!props.apiData || !Array.isArray(props.apiData)) {
    return false;
  }
  
  if (isShowingDongs.value) {
    // 읍면동 모드: API 응답의 읍면동 코드와 매칭
    // 1. 정확한 매칭 시도
    let isActive = props.apiData.some(item => item.dongCode === originalBjcd);
    
    if (!isActive) {
      // 2. BJCD 앞 8자리로 매칭 시도 (마지막 자리수 차이 처리)
      isActive = props.apiData.some(item => {
        const apiCode = item.dongCode;
        if (!apiCode) return false;
        // 앞 8자리가 같은지 확인 (마지막 2자리는 무시)
        return apiCode.substring(0, 8) === originalBjcd.substring(0, 8);
      });
      
      // if (isActive) {
      //   console.log(`읍면동 매칭 성공 (앞 8자리): ${originalBjcd}`);
      // }
    }
    return isActive;
  } else if (isShowingSubRegions.value) {
    // 시군구 모드: API 응답의 시군구 코드와 매칭
    // BJCD에서 시군구 코드 추출 (예: 4111000000 -> 110)
    const sigunguCode = originalBjcd.substring(2, 5);
    return props.apiData.some(item => item.sigunguCode === sigunguCode);
  } else {
    // 시도 모드: API 응답의 시도 코드와 매칭
    return props.apiData.some(item => item.cityCode === originalBjcd);
  }
};

// 지역명 매칭 함수 (지도 지역명을 카드 지역명으로 변환)
const getMatchingCardRegionName = (mapRegionName: string) => {
  // 지도 지역명과 카드 지역명 매칭
  const regionMapping: { [key: string]: string } = {
    '서울특별시': '서울',
    '부산광역시': '부산',
    '대구광역시': '대구',
    '인천광역시': '인천',
    '광주광역시': '광주',
    '대전광역시': '대전',
    '울산광역시': '울산',
    '세종특별자치시': '세종',
    '경기도': '경기',
    '강원특별자치도': '강원',
    '충청북도': '충북',
    '충청남도': '충남',
    '전라북도': '전북',
    '전라남도': '전남',
    '경상북도': '경북',
    '경상남도': '경남',
    '제주특별자치도': '제주'
  };
  
  const result = regionMapping[mapRegionName] || mapRegionName;
  // console.log('지역명 매칭:', mapRegionName, '→', result);
  return result;
};

// 지도 마우스 오버 핸들러 (같은 지역의 모든 영역 함께 하이라이트)
const handleRegionMouseOver = (regionData: any) => {
  // console.log('지도 마우스 오버:', regionData.regionName, '활성화 상태:', isRegionActive(regionData.regionCode));
  
  if (isRegionActive(regionData.regionCode)) {
    let cardRegionName = '';
    
    if (isShowingDongs.value) {
      // 읍면동 모드: API 데이터에서 해당 읍면동명 찾기
      const apiItem = props.apiData?.find((item: any) => {
        const apiCode = item.dongCode;
        if (!apiCode) return false;
        // 정확한 매칭 또는 앞 8자리 매칭
        return apiCode === regionData.regionCode || 
               apiCode.substring(0, 8) === regionData.regionCode.substring(0, 8);
      });
      cardRegionName = apiItem?.dongNameAlias || regionData.regionName;
      // console.log('읍면동 모드 - API 데이터에서 찾은 지역명:', cardRegionName);
      
    } else if (isShowingSubRegions.value) {
      // 시군구 모드: API 데이터에서 해당 시군구명 찾기
      const originalBjcd = regionData.regionCode.replace(/_\d+$/, '');
      const sigunguCode = originalBjcd.substring(2, 5);
      const apiItem = props.apiData?.find((item: any) => item.sigunguCode === sigunguCode);
      cardRegionName = apiItem?.sigunguName || regionData.regionName;
      // console.log('시군구 모드 - API 데이터에서 찾은 지역명:', cardRegionName);
      
    } else {
      // 시도 모드: 기존 로직 사용
      cardRegionName = getMatchingCardRegionName(regionData.regionName);
      // console.log('시도 모드 - 변환된 지역명:', cardRegionName);
    }
    
    hoveredRegion.value = cardRegionName;
    // console.log('호버된 지역 설정:', hoveredRegion.value);
    
    // 동일한 이름을 가진 모든 지역 찾기 (전국 지도에서)
    if (!isShowingSubRegions.value && !isShowingDongs.value) {
      const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
        path.regionName === regionData.regionName
      );
      
      // 동일한 이름의 지역들을 모두 하이라이트
      sameNameRegions.forEach((path: any) => {
        const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
        if (pathElement) {
          pathElement.style.fill = '#2E5BBA';
          pathElement.style.stroke = '#1A4A8A';
          pathElement.style.strokeWidth = '3';
          pathElement.style.opacity = '0.95';
        }
      });
    }
  }
};

// 지도 마우스 아웃 핸들러 (같은 지역의 모든 영역에서 하이라이트 제거)
const handleRegionMouseOut = (regionData: any) => {
  console.log('지도 마우스 아웃:', regionData.regionName);
  hoveredRegion.value = '';
  
  // 동일한 이름을 가진 모든 지역 찾기 (전국 지도에서)
  if (!isShowingSubRegions.value && !isShowingDongs.value) {
    const sameNameRegions = nationalMapSvgPaths.value.filter((path: any) => 
      path.regionName === regionData.regionName
    );
    
    // 동일한 이름의 지역들의 하이라이트 제거
    sameNameRegions.forEach((path: any) => {
      const pathElement = document.querySelector(`[data-region-code="${path.regionCode}"]`) as SVGPathElement;
      if (pathElement) {
        pathElement.style.fill = '#D1E7F5';
        pathElement.style.stroke = '#4A90E2';
        pathElement.style.strokeWidth = '2';
        pathElement.style.opacity = '0.85';
      }
    });
  }
};

// 카드 지역명을 지도 지역명으로 변환하는 함수
const getMatchingMapRegionName = (cardRegionName: string) => {
  const reverseMapping: { [key: string]: string } = {
    '서울': '서울특별시',
    '부산': '부산광역시',
    '대구': '대구광역시',
    '인천': '인천광역시',
    '광주': '광주광역시',
    '대전': '대전광역시',
    '울산': '울산광역시',
    '세종': '세종특별자치시',
    '경기': '경기도',
    '강원': '강원특별자치도',
    '충북': '충청북도',
    '충남': '충청남도',
    '전북': '전라북도',
    '전남': '전라남도',
    '경북': '경상북도',
    '경남': '경상남도',
    '제주': '제주특별자치도'
  };
  
  return reverseMapping[cardRegionName] || cardRegionName;
};

// 데이터 카드 마우스 오버 핸들러
const handleCardMouseOver = (region: any) => {
  // console.log('카드 마우스 오버:', region.cityNameAlias);
  hoveredRegion.value = region.cityNameAlias;
  // console.log('hoveredRegion 설정됨:', hoveredRegion.value);
  
  // 시군구/읍면동 모드에서도 지도와 연동
  if (isShowingDongs.value || isShowingSubRegions.value) {
    // console.log('시군구/읍면동 모드에서 카드 호버 - 지도 연동');
  }
  
  // 강제로 반응성 업데이트 트리거
  nextTick(() => {
    // console.log('nextTick 후 hoveredRegion:', hoveredRegion.value);
  });
};

// 데이터 카드 마우스 아웃 핸들러
const handleCardMouseOut = (region: any) => {
  // console.log('카드 마우스 아웃:', region.cityNameAlias);
  hoveredRegion.value = '';
};

// 지역의 배경색을 결정하는 함수
const getRegionFillColor = (regionCode: string, regionName: string) => {
  if (isShowingDongs.value || isShowingSubRegions.value) {
    // 시군구/읍면동 모드에서는 API 데이터가 있는 지역만 활성화
    const isActive = isRegionActive(regionCode);
    if (!isActive) {
      if (isShowingDongs.value) {
        console.log(`읍면동 비활성화: ${regionName} (${regionCode})`);
      }
      return '#E0E0E0'; // 비활성화된 지역은 회색
    }
    
    if (regionName === selectedRegion.value) {
      return '#1A4A8A'; // 선택된 지역 (가장 진한 파란색)
    }
    
    if (regionName === hoveredRegion.value) {
      console.log('시군구/읍면동 지도 호버 매칭됨:', regionName, '===', hoveredRegion.value);
      return '#3A7BC8'; // 호버된 지역 (중간 파란색)
    }
    
    return '#D1E7F5'; // 기본 지역
  } else {
    // 시도 모드 - 선택된 지역 표시 제거
    if (!isRegionActive(regionCode)) {
      return '#E0E0E0'; // 비활성화된 지역은 회색
    }
    
    const cardRegionName = getMatchingCardRegionName(regionName);
    
    if (cardRegionName === hoveredRegion.value) {
      console.log('시도 지도 호버 매칭됨:', regionName, '→', cardRegionName, '===', hoveredRegion.value);
      return '#3A7BC8'; // 호버된 지역 (중간 파란색)
    }
    
    return '#D1E7F5'; // 기본 지역
  }
};

// 지역의 stroke 색상을 결정하는 함수
const getRegionStrokeColor = (regionCode: string, regionName: string) => {
  if (isShowingDongs.value || isShowingSubRegions.value) {
    // 시군구/읍면동 모드에서는 API 데이터가 있는 지역만 활성화
    if (!isRegionActive(regionCode)) {
      return '#B0B0B0'; // 비활성화된 지역은 연한 회색 테두리
    }
    
    if (regionName === selectedRegion.value) {
      return '#1A4A8A'; // 선택된 지역
    }
    
    // API 데이터에서 찾은 지역명과 비교
    let cardRegionName = '';
    if (isShowingDongs.value) {
      // 읍면동 모드: API 데이터에서 해당 읍면동명 찾기
      const apiItem = props.apiData?.find((item: any) => {
        const apiCode = item.dongCode;
        if (!apiCode) return false;
        return apiCode === regionCode || 
               apiCode.substring(0, 8) === regionCode.substring(0, 8);
      });
      cardRegionName = apiItem?.dongNameAlias || regionName;
    } else if (isShowingSubRegions.value) {
      // 시군구 모드: API 데이터에서 해당 시군구명 찾기
      const originalBjcd = regionCode.replace(/_\d+$/, '');
      const sigunguCode = originalBjcd.substring(2, 5);
      const apiItem = props.apiData?.find((item: any) => item.sigunguCode === sigunguCode);
      cardRegionName = apiItem?.sigunguName || regionName;
    }
    
    if (cardRegionName === hoveredRegion.value) {
      return '#1A4A8A'; // 호버된 지역 (더 진한 테두리)
    }
    
    return '#4A90E2'; // 기본 지역
  } else {
    // 시도 모드 - 선택된 지역 표시 제거
    if (!isRegionActive(regionCode)) {
      return '#B0B0B0'; // 비활성화된 지역은 연한 회색 테두리
    }
    
    const cardRegionName = getMatchingCardRegionName(regionName);
    
    if (cardRegionName === hoveredRegion.value) {
      console.log('시도 지도 stroke 호버 매칭됨:', regionName, '→', cardRegionName, '===', hoveredRegion.value);
      return '#1A4A8A'; // 호버된 지역 (더 진한 테두리)
    }
    
    return '#4A90E2'; // 기본 지역
  }
};

// 지역의 opacity를 결정하는 함수
const getRegionOpacity = (regionCode: string, regionName: string) => {
  if (isShowingDongs.value || isShowingSubRegions.value) {
    // 시군구/읍면동 모드에서는 API 데이터가 있는 지역만 활성화
    if (!isRegionActive(regionCode)) {
      return 0.5; // 비활성화된 지역은 반투명
    }
    
    if (regionName === selectedRegion.value) {
      return 0.95; // 선택된 지역
    }
    
    // API 데이터에서 찾은 지역명과 비교
    let cardRegionName = '';
    if (isShowingDongs.value) {
      // 읍면동 모드: API 데이터에서 해당 읍면동명 찾기
      const apiItem = props.apiData?.find((item: any) => {
        const apiCode = item.dongCode;
        if (!apiCode) return false;
        return apiCode === regionCode || 
               apiCode.substring(0, 8) === regionCode.substring(0, 8);
      });
      cardRegionName = apiItem?.dongNameAlias || regionName;
    } else if (isShowingSubRegions.value) {
      // 시군구 모드: API 데이터에서 해당 시군구명 찾기
      const originalBjcd = regionCode.replace(/_\d+$/, '');
      const sigunguCode = originalBjcd.substring(2, 5);
      const apiItem = props.apiData?.find((item: any) => item.sigunguCode === sigunguCode);
      cardRegionName = apiItem?.sigunguName || regionName;
    }
    
    if (cardRegionName === hoveredRegion.value) {
      return 1.0; // 호버된 지역
    }
    
    return 0.85; // 기본 지역
  } else {
    // 시도 모드 - 선택된 지역 표시 제거
    if (!isRegionActive(regionCode)) {
      return 0.5; // 비활성화된 지역은 반투명
    }
    
    const cardRegionName = getMatchingCardRegionName(regionName);
    
    if (cardRegionName === hoveredRegion.value) {
      console.log('시도 지도 opacity 호버 매칭됨:', regionName, '→', cardRegionName, '===', hoveredRegion.value);
      return 1.0; // 호버된 지역
    }
    
    return 0.85; // 기본 지역
  }
};

// 카드 클릭 핸들러
const handleRegionCardClick = (region: any) => {
  console.log('카드 클릭:', region);
  
  // 사용자 액션으로 인한 네비게이션임을 표시
  isUserNavigation.value = true;
  
  if (isShowingDongs.value) {
    // 읍면동 모드에서는 선택된 지역 설정
    selectedRegion.value = region.cityNameAlias;
    isMapSelected.value = true;
    
    // 해당 지역을 지도에서 하이라이트
    highlightRegionOnMap(region.cityCode);
    
    // 지도에서 해당 지역으로 스크롤 (필요시)
    scrollToRegionOnMap(region.cityCode);
  } else if (isShowingSubRegions.value) {
    // 시군구 모드에서는 읍면동으로 이동
    // console.log('시군구 카드 클릭 - 읍면동으로 이동:', region);
    
    // 시군구 코드 추출 (originalData에서 실제 API 데이터 사용)
    const originalData = region.originalData;
    const sigunguCode = originalData?.sigunguCode || region.cityCode; // cityCode는 sigunguCode로 설정됨
    const sigunguName = originalData?.sigunguName || region.cityNameAlias;
    
    // console.log('원본 데이터:', originalData);
    // console.log('시군구 코드:', sigunguCode);
    // console.log('시군구 이름:', sigunguName);
    
    if (sigunguCode) {
      // 시군구 코드를 5자리로 변환 (시도코드 + 시군구코드)
      const cityCode = selectedRegionCode.value; // 현재 선택된 시도 코드
      const fullSigunguCode = cityCode + sigunguCode; // 5자리 시군구 코드
      
      // console.log(`🖱️ 시군구 카드 클릭: ${sigunguName} (${sigunguCode}) - 읍면동으로 이동`);
      // console.log(`📍 시도 코드: ${cityCode}, 시군구 코드: ${sigunguCode}, 전체 코드: ${fullSigunguCode}`);
      
      // 읍면동 로드 (5자리 코드 사용)
      loadDongs(fullSigunguCode, sigunguName);
      
      // API 파라미터 업데이트 (5자리 코드 사용)
      // console.log(`🔄 API 파라미터 업데이트: parentDongCode=${fullSigunguCode}, groupLevel=dong`);
      updateApiParameters(fullSigunguCode, 'dong');
      
      // 이벤트 발생 (5자리 코드 사용)
      emit('regionSelected', {
        regionCode: fullSigunguCode,
        regionName: sigunguName,
        regionEng: region.regionEng || '',
        zlocId: `sigungu_${fullSigunguCode}`,
        zlocType: 'sigungu'
      });
    } else {
      // console.warn('시군구 코드를 찾을 수 없습니다:', region);
    }
  } else {
    // 시도 모드에서는 선택된 지역 상태를 설정하지 않음 (시군구로 바로 이동)
    // console.log('시도 카드 클릭 - 시군구로 이동');
    // 시도 클릭과 동일한 로직 실행
    handleRegionClick({
      regionCode: region.cityCode,
      regionName: region.cityNameAlias,
      regionEng: region.regionEng || '',
      zlocId: `city_${region.cityCode}`,
      zlocType: 'city'
    });
  }
};

// 지도에서 지역 하이라이트 함수
const highlightRegionOnMap = (regionCode: string) => {
  // 기존 하이라이트 제거
  clearAllHighlights();
  
  // 새로운 지역 하이라이트
  const pathElement = document.querySelector(`[data-region-code="${regionCode}"]`) as SVGPathElement;
  if (pathElement) {
    pathElement.style.fill = '#2E5BBA';
    pathElement.style.stroke = '#1A4A8A';
    pathElement.style.strokeWidth = '3';
    pathElement.style.opacity = '0.95';
    console.log(`지역 하이라이트: ${regionCode} - 요소 찾음`);
  } else {
    console.warn(`지역 하이라이트: ${regionCode} - 요소를 찾을 수 없음`);
  }
};

// 지도에서 해당 지역으로 스크롤하는 함수
const scrollToRegionOnMap = (regionCode: string) => {
  const pathElement = document.querySelector(`[data-region-code="${regionCode}"]`) as SVGPathElement;
  if (pathElement) {
    // SVG 요소의 경계 상자 계산
    const bbox = pathElement.getBBox();
    const svgElement = pathElement.closest('svg');
    
    if (svgElement) {
      // SVG 뷰포트 크기
      const svgRect = svgElement.getBoundingClientRect();
      const viewBox = svgElement.viewBox.baseVal;
      
      // 지역 중심점 계산
      const centerX = bbox.x + bbox.width / 2;
      const centerY = bbox.y + bbox.height / 2;
      
      // 뷰포트 중심으로 이동하기 위한 변환 계산
      const translateX = viewBox.width / 2 - centerX;
      const translateY = viewBox.height / 2 - centerY;
      
      // 현재 변환에 추가 변환 적용
      const mapContainer = document.querySelector('.korea-map') as HTMLElement;
      if (mapContainer) {
        const currentTransform = mapContainer.style.transform || '';
        const newTransform = `translate(${translateX}px, ${translateY}px)`;
        mapContainer.style.transform = newTransform;
        
        // 부드러운 애니메이션을 위한 트랜지션 추가
        mapContainer.style.transition = 'transform 0.5s ease-in-out';
        
        // 애니메이션 완료 후 트랜지션 제거
        setTimeout(() => {
          mapContainer.style.transition = '';
        }, 500);
      }
    }
  }
};

// 모든 하이라이트 제거
const clearAllHighlights = () => {
  const allPaths = document.querySelectorAll('.map-path');
  allPaths.forEach((path: any) => {
    path.style.fill = '#D1E7F5';
    path.style.stroke = '#4A90E2';
    path.style.strokeWidth = '2';
    path.style.opacity = '0.85';
  });
};

// 사용자 권한 확인 함수
const canGoBackToSigungu = () => {
  const user = userInfo.value;
  if (!user || !user.areaKind) return true; // 사용자 정보가 없으면 기본적으로 허용
  
  // 읍면동 권한 사용자는 시군구로 갈 수 없음
  return user.areaKind !== 'dong';
};

const canGoBackToNational = () => {
  const user = userInfo.value;
  if (!user || !user.areaKind) return true; // 사용자 정보가 없으면 기본적으로 허용
  
  // 시군구 권한 사용자는 시도로 갈 수 없음
  return user.areaKind !== 'sigungu';
};

// 뒤로가기 버튼 클릭 핸들러
const handleBackButtonClick = () => {
  if (isShowingDongs.value) {
    // 읍면동에서 시군구로 돌아가기 (권한 확인)
    if (canGoBackToSigungu()) {
      backToSigunguMap();
    } else {
      // console.log('권한 없음: 읍면동 권한 사용자는 시군구로 갈 수 없습니다.');
    }
  } else if (isShowingSubRegions.value) {
    // 시군구에서 시도로 돌아가기 (권한 확인)
    if (canGoBackToNational()) {
      backToNationalMap();
    } else {
      // console.log('권한 없음: 시군구 권한 사용자는 시도로 갈 수 없습니다.');
    }
  }
};

// 뒤로가기 버튼 표시 여부 확인
const shouldShowBackButton = () => {
  // 초기 로드가 완료되지 않았으면 뒤로가기 버튼을 표시하지 않음
  if (!isInitialLoadCompleted.value) {
    return false;
  }
  
  // 현재 상태와 최초 로드 단계를 비교하여 뒤로가기 버튼 표시 여부 결정
  if (isShowingDongs.value) {
    // 읍면동 모드: 최초 로드가 'dong'이 아니면 뒤로가기 버튼 표시
    if (initialLoadLevel.value !== 'dong') {
      return canGoBackToSigungu(); // 읍면동에서 시군구로 갈 권한이 있는지 확인
    }
    return false; // 최초 로드가 읍면동이면 뒤로가기 버튼 숨김
  } else if (isShowingSubRegions.value) {
    // 시군구 모드: 최초 로드가 'sigungu'가 아니면 뒤로가기 버튼 표시
    if (initialLoadLevel.value !== 'sigungu') {
      return canGoBackToNational(); // 시군구에서 시도로 갈 권한이 있는지 확인
    }
    return false; // 최초 로드가 시군구이면 뒤로가기 버튼 숨김
  }
  return false;
};

// 뒤로가기 버튼 텍스트 반환
const getBackButtonText = () => {
  if (isShowingDongs.value) {
    return '시군구로';
  } else if (isShowingSubRegions.value) {
    return '시도로';
  }
  return '뒤로';
};

// 뒤로가기 버튼 툴팁 반환
const getBackButtonTitle = () => {
  if (isShowingDongs.value) {
    return canGoBackToSigungu() ? '시군구로 돌아가기' : '권한이 없습니다';
  } else if (isShowingSubRegions.value) {
    return canGoBackToNational() ? '시도로 돌아가기' : '권한이 없습니다';
  }
  return '뒤로가기';
};


</script>

<template>
  <div class="map-render common-card map-dashboard p-1 h-100">
    <div class="col-12 p-1 h-100">
      <div class="map-container h-100">

        <!-- 뒤로가기 버튼 -->
        <div v-if="shouldShowBackButton()" class="back-button-container">
          <button 
            @click="handleBackButtonClick"
            class="back-button"
            :title="getBackButtonTitle()"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {{ getBackButtonText() }}
          </button>
        </div>

        <!-- 우측 데이터 카드 영역 -->
        <div class="data-panel right-panel">
          <div class="data-cards-vertical">
            <div 
              v-for="(region, i) in rightPanelData" 
              :key="`right-region-${i}`"
              class="region-card-vertical" 
              :class="{ 
                'selected': region.cityNameAlias === selectedRegion,
                'hovered': region.cityNameAlias === hoveredRegion
              }"
              :data-region-code="region.cityCode"
              @click="handleRegionCardClick(region)"
              @mouseenter="handleCardMouseOver(region)"
              @mouseleave="handleCardMouseOut(region)"
            >
              <div class="card-header">
                <div class="region-info">
                  <span class="region-name">{{ region.cityNameAlias }}</span>
                  <!-- <span class="region-code">{{ region.cityCode }}</span> -->
                </div>
                <div class="equip-count">
                  <span class="count">{{ commonUtils.isAllStrDataEmpty(region.eqCount) }}</span>
                  <span class="unit">개</span>
                </div>
              </div>
              
              <div class="card-body">
                <div class="data-section">
                  <div class="data-row">
                    <span class="label">금일 발전량</span>
                    <div class="value-group">
                      <span class="value">{{ commonUtils.isAllStrDataEmpty(region.todayProdKWH) }}</span>
                      <span class="unit">kWh</span>
                    </div>
                  </div>
                  <div class="data-row">
                    <span class="label">금일 사용량</span>
                    <div class="value-group">
                      <span class="value">{{ commonUtils.isAllStrDataEmpty(region.todayUsageKWH) }}</span>
                      <span class="unit">kWh</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 중앙 지도 영역 -->
        <div class="map-area" @click="handleMapAreaClick">
          
          <div class="map-wrapper">
          <!-- 전국 지도 SVG -->
          <svg 
            v-if="!isShowingSubRegions"
            class="korea-map" 
            viewBox="0 0 1000 900" 
            width="100%" 
            height="100%"
            xmlns="http://www.w3.org/2000/svg"
            style="position: absolute; top: 0; left: -70; z-index: 1; min-height: 100%;"
            preserveAspectRatio="xMidYMid meet"
            @wheel="handleWheel"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @click="handleMapBackgroundClick"
          >
              <!-- 전국 지도 path들 (고성능 최적화) -->
              <g 
                :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
                @mousedown="handleMouseDown"
                @touchstart="handleTouchStart"
              >
                <template v-for="regionPath in nationalMapSvgPaths" :key="regionPath.regionCode">
                  <path
                    v-if="regionPath.svgPath && regionPath.svgPath.length > 0"
                    :d="regionPath.svgPath"
                    :fill="getRegionFillColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke="getRegionStrokeColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke-width="getMatchingCardRegionName(regionPath.regionName) === hoveredRegion ? '2.5' : '2'"
                    :opacity="getRegionOpacity(regionPath.regionCode, regionPath.regionName)"
                    class="map-path"
                    :class="{ 
                      hovered: getMatchingCardRegionName(regionPath.regionName) === hoveredRegion,
                      inactive: !isRegionActive(regionPath.regionCode)
                    }"
                    @click="handleRegionClick(regionPath)"
                    @mouseover="handleRegionMouseOver(regionPath)"
                    @mouseout="handleRegionMouseOut(regionPath)"
                    :data-region-code="regionPath.regionCode"
                    :data-region-name="regionPath.regionName"
                    :key="`${regionPath.regionCode}-${forceUpdate}`"
                  />
                </template>
              </g>
              
      
              
              <!-- 전국 지도에서는 복잡한 요소들 제거 (단순한 경계만 표시) -->
              
          </svg>

          <!-- 시군구 지도 SVG (하위 지역 표시 시) -->
          <svg 
            v-if="isShowingSubRegions"
            class="sigungu-map" 
            viewBox="0 0 1000 900" 
            width="100%" 
            height="100%"
            xmlns="http://www.w3.org/2000/svg"
            style="position: absolute; top: 0; left: -70; z-index: 1; min-height: 100%;"
            preserveAspectRatio="xMidYMid meet"
            @wheel="handleWheel"
            @click="handleMapBackgroundClick"
          >
              <!-- 시군구 지도 path들 -->
              <g 
                :style="{ cursor: 'default' }"
              >
                <template v-for="regionPath in nationalMapSvgPaths" :key="regionPath.regionCode">
                  <path
                    v-if="regionPath.svgPath && regionPath.svgPath.length > 0"
                    :d="regionPath.svgPath"
                    :fill="getRegionFillColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke="getRegionStrokeColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke-width="regionPath.regionName === selectedRegion ? '3' : (regionPath.regionName === hoveredRegion ? '2.5' : '2')"
                    :opacity="getRegionOpacity(regionPath.regionCode, regionPath.regionName)"
                    class="sigungu-path"
                    :class="{ 
                      selected: regionPath.regionName === selectedRegion,
                      hovered: regionPath.regionName === hoveredRegion,
                      inactive: !isRegionActive(regionPath.regionCode)
                    }"
                    :data-region-code="regionPath.regionCode"
                    :data-region-name="regionPath.regionName"
                    @click="handleSigunguClick($event, regionPath)"
                    @mouseover="handleSigunguMouseOver($event, regionPath)"
                    @mouseout="handleSigunguMouseOut($event, regionPath)"
                    :key="`sigungu-${regionPath.regionCode}-${forceUpdate}`"
                  />
                </template>
              </g>
              
          </svg>

          <!-- 읍면동 지도 SVG (읍면동 표시 시) -->
          <svg 
            v-if="isShowingDongs"
            class="dong-map" 
            viewBox="0 0 1000 900" 
            width="100%" 
            height="100%"
            xmlns="http://www.w3.org/2000/svg"
            style="position: absolute; top: 0; left: -70; z-index: 2; min-height: 100%;"
            preserveAspectRatio="xMidYMid meet"
            @wheel="handleWheel"
            @click="handleMapBackgroundClick"
          >
              <!-- 읍면동 지도 path들 -->
              <g 
                :style="{ cursor: 'default' }"
              >
                <template v-for="regionPath in nationalMapSvgPaths" :key="regionPath.regionCode">
                  <path
                    v-if="regionPath.svgPath && regionPath.svgPath.length > 0"
                    :d="regionPath.svgPath"
                    :fill="getRegionFillColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke="getRegionStrokeColor(regionPath.regionCode, regionPath.regionName)"
                    :stroke-width="regionPath.regionName === selectedRegion ? '2.5' : (regionPath.regionName === hoveredRegion ? '2' : '1.5')"
                    :opacity="getRegionOpacity(regionPath.regionCode, regionPath.regionName)"
                    class="dong-path"
                    :class="{ 
                      selected: regionPath.regionName === selectedRegion,
                      hovered: regionPath.regionName === hoveredRegion,
                      inactive: !isRegionActive(regionPath.regionCode)
                    }"
                    :data-region-code="regionPath.regionCode"
                    :data-region-name="regionPath.regionName"
                    @click="handleDongClick($event, regionPath)"
                    @mouseover="handleDongMouseOver($event, regionPath)"
                    @mouseout="handleDongMouseOut($event, regionPath)"
                    :key="`dong-${regionPath.regionCode}-${forceUpdate}`"
                  />
                </template>
              </g>
          </svg>
          </div>


        </div>

      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-toc/define_font.scss";
@use "@/assets/styles/design-toc/map-dashboard.scss";

// 통합된 지도 path 스타일
.map-path,
.sigungu-map .sigungu-path,
.dong-map .dong-path {
  fill: #D1E7F5;
  stroke: #4A90E2;
  stroke-width: 2;
  opacity: 0.85;
  cursor: pointer;
  transform-origin: center;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: all 0.2s ease-out;
  
  &:hover {
    fill: #4A90E2 !important;
    stroke: #2E5BBA !important;
    stroke-width: 4 !important;
    opacity: 1 !important;
    filter: brightness(1.15) drop-shadow(0 5px 10px rgba(46, 91, 186, 0.4)) !important;
  }
  
  &:active {
    fill: #1A4A8A !important;
    stroke: #0F2F5C !important;
    stroke-width: 3.5 !important;
    opacity: 0.95 !important;
    filter: brightness(0.9) drop-shadow(0 2px 4px rgba(15, 47, 92, 0.4)) !important;
    transform: scale(0.98) !important;
    transition: all 0.1s ease !important;
  }
  
  // 호버된 지역 스타일 (카드에서 호버 시)
  &.hovered {
    fill: #3A7BC8 !important;
    stroke: #1A4A8A !important;
    stroke-width: 2.5 !important;
    opacity: 1.0 !important;
    animation: hoverGlow 1.5s ease-in-out infinite !important;
  }
  
  // 비활성화된 지역 스타일
  &.inactive {
    fill: #E0E0E0 !important;
    stroke: #B0B0B0 !important;
    stroke-width: 1.5 !important;
    opacity: 0.4 !important;
    cursor: not-allowed !important;
    filter: grayscale(0.8) !important;
    
    &:hover {
      fill: #E0E0E0 !important;
      stroke: #B0B0B0 !important;
      stroke-width: 1.5 !important;
      opacity: 0.4 !important;
      filter: grayscale(0.8) !important;
    }
  }
}

// 뒤로가기 버튼 스타일
.back-button-container {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  padding: 12px 20px;
  color: #4A90E2;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(15px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-3px) scale(1.05);
    box-shadow: 
      0 15px 35px rgba(0, 0, 0, 0.15),
      0 8px 20px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    border-color: rgba(102, 126, 234, 0.5);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.1),
      0 4px 10px rgba(0, 0, 0, 0.05);
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(-2px);
  }
}

// 읍면동 지도만 stroke-width 조정
.dong-map .dong-path {
  stroke-width: 1.5;
  opacity: 0.8;
  
  &:hover {
    stroke-width: 2.5 !important;
  }
  
  &:active {
    stroke-width: 3 !important;
  }
}


// 통합된 라벨 스타일
.sigungu-labels .sigungu-label,
.dong-labels .dong-label {
  font-family: 'Noto Sans KR', sans-serif;
  text-shadow: 2px 2px 4px rgba(255, 255, 255, 0.9), -1px -1px 2px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  user-select: none;
  font-size: 12px;
  font-weight: bold;
  fill: #0F2F5C !important;
  
  // 작은 시군구는 더 작은 폰트
  &[data-region-code*="11"] { font-size: 11px; } // 서울
  &[data-region-code*="26"] { font-size: 12px; } // 부산
  &[data-region-code*="27"] { font-size: 12px; } // 대구
}

// 읍면동 라벨만 더 작은 폰트
.dong-labels .dong-label {
  font-size: 8px;
}

// 우측 패널 스타일
.right-panel {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 1rem;
  width: 450px;
  z-index: 100;
  overflow-y: auto;
  

  
  // ANCHOR: 스크롤바 스타일링
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #4A90E2;
    border-radius: 3px;
    
    &:hover {
      background: #2E5BBA;
    }
  }
  
  // 패널 헤더 제거됨
  
  .data-cards-vertical {
    height: 100%;
    padding: 0.725rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
  }
  
  .region-card-vertical {
    background: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
    
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }
    
    &.selected {
      box-shadow: 0 4px 20px rgba(74, 144, 226, 0.3);
      transform: translateY(-2px);
    }
    
    &.hovered {
      box-shadow: 0 8px 32px rgba(74, 144, 226, 0.4) !important;
      transform: translateY(-4px) scale(1.02) !important;
      z-index: 10 !important;
      
      .card-header {
        background: linear-gradient(135deg, #5BA0F2 0%, #4A90E2 100%) !important;
        box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3) !important;
      }
      
      .card-body {
        background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%) !important;
      }
    }
    
    .card-header {
      background: #4A90E2;
      padding: 8px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .region-info {
        .region-name {
          @include define_font.text-bold-18;
          color: #ffffff;
          margin: 0;
        }
      }
      
      .equip-count {
        color: #ffffff;
        font-size: 14px;
        font-weight: 500;
        
        .count {
          font-weight: 700;
        }
      }
    }
    
    .card-body {
      background: #ffffff;
      padding: 10px;
      
      .data-section {
        margin-bottom: 0;
        
        .data-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .label {
            font-size: 14px;
            color: #333333;
            font-weight: 500;
          }
          
          .value-group {
            display: flex;
            align-items: baseline;
            gap: 4px;
            
            .value {
              font-size: 18px;
              font-weight: 700;
              color: #000000;
            }
            
            .unit {
              font-size: 12px;
              color: #666666;
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}


// ANCHOR: SVG 컨테이너 스타일 (고성능 최적화)
.korea-map, .sigungu-map, .dong-map {
  user-select: none;
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  contain: layout style paint; // 렌더링 최적화
  object-fit: contain; // 비율 유지하면서 컨테이너에 맞춤
  transform-origin: center center; // 중심점 기준으로 축소
  
  // 지도 경로 호버 효과
  path {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    
    &:hover {
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
      transform: scale(1.02);
      transform-origin: center;
    }
  }
}

// 지도 스타일 - 시군구와 읍면동 지도만 100% 크기로 조정하고 중앙 정렬
.sigungu-map, .dong-map {
  transform: scale(1.0) translateY(0%);
  transform-origin: center center;
}


// 새로운 레이아웃 컨테이너
.map-container {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden; // 지도가 컨테이너를 벗어나지 않도록 hidden으로 복원
  border-radius: 10px;
  backdrop-filter: blur(10px);
}

// 데이터 패널 스타일
.data-panel {
  width: 260px;
  max-height: calc(100vh - 155px);
  min-height: 200px; // 최소 높이 설정
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 1rem;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 15px 30px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  overflow: hidden;
  padding: 0px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 20;
  position: absolute;
  
  &.left-panel {
    left: 20px;
    bottom: 20px;
    top: auto;
    border-left: 4px solid #4A90E2;
  }
  
  // 스크롤바 스타일링
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(74, 144, 226, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(74, 144, 226, 0.5);
    }
  }
}

// 스크롤 인디케이터 스타일
.scroll-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(74, 144, 226, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 4px;
  animation: pulse 2s ease-in-out infinite;
  
  .scroll-arrow {
    font-size: 12px;
  }
  
  &.top {
    top: 10px;
  }
  
  &.bottom {
    bottom: 10px;
  }
}

.data-cards {
  display: flex;
  flex-direction: column;
  gap: 12px; // 20px에서 12px로 줄임 (더 많은 카드 표시)
  animation: fadeInUp 0.6s ease-out;
}

.region-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  backdrop-filter: blur(15px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  min-height: 90px; // 최소 높이 설정
  
  &:hover {
    transform: translateY(-6px) scale(1.03);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 10px 20px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    border-color: rgba(102, 126, 234, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &.selected {
    border-color: #4A90E2;
    background: rgba(248, 251, 255, 0.95);
    box-shadow: 0 8px 24px rgba(74, 144, 226, 0.2);
    transform: translateY(-2px);
    
    &::before {
    opacity: 1;
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  // margin-bottom: 8px; // 12px에서 8px로 줄임
  
  .region-name {
    font-size: 14px; // 16px에서 14px로 줄임
    font-weight: bold;
    color: #2E5BBA;
    line-height: 1.2;
  }
  
  .equip-count {
    text-align: right;
    
    .label {
      display: block;
      font-size: 10px; // 12px에서 10px로 줄임
      color: #666;
      margin-bottom: 1px; // 2px에서 1px로 줄임
    }
    
    .count {
      font-size: 12px; // 14px에서 12px로 줄임
      font-weight: bold;
    }
  }
}

.card-body {
  .data-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px; // 8px에서 6px로 줄임
    
    .label {
      font-size: 11px; // 13px에서 11px로 줄임
      color: #666;
    }
    
    .value-group {
      display: flex;
      align-items: baseline;
      
      .value {
        font-size: 12px; // 14px에서 12px로 줄임
        font-weight: bold;
        color: #333;
      }
      
      .unit {
        font-size: 10px; // 12px에서 10px로 줄임
        color: #888;
        margin-left: 3px; // 4px에서 3px로 줄임
      }
    }
  }
}

// 지도 영역 스타일 조정 (고성능 최적화)
.map-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; // 지도가 컨테이너를 벗어나지 않도록 hidden으로 복원
  height: 100%;
  contain: layout; // 레이아웃 최적화
  padding: 10px; // 적절한 여백 추가
  background-color: #FFF;
}

.map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  contain: layout; // 레이아웃 최적화
  max-width: 100%;
  max-height: 100%;
}




// 지역 경계 상자 스타일
.region-bounding-boxes {
  .region-bbox {
    transition: stroke-width 0.3s ease, opacity 0.3s ease;
    
    &:hover {
      stroke-width: 3;
      opacity: 1;
    }
  }
  
  .region-bbox-label {
    pointer-events: none;
    user-select: none;
  }
}

// 데이터 연결선 스타일
.data-connection-lines {
  .connection-line {
    transition: stroke-width 0.3s ease, opacity 0.3s ease;
    animation: connectionPulse 3s ease-in-out infinite;
    stroke-width: 2;
    opacity: 0.8;
    
    &:hover {
      stroke-width: 3;
      opacity: 1;
    }
    
    &.selected {
      stroke: #ff6b6b;
      stroke-width: 3;
      opacity: 1;
      animation: selectedPulse 1.5s ease-in-out infinite;
    }
  }
}

// 지역 중심점 스타일
.region-centers {
  .region-center-dot {
    transition: r 0.3s ease, fill 0.3s ease;
    animation: pulse 2s ease-in-out infinite;
    
    &:hover {
      r: 7;
      fill: #2E5BBA;
    }
  }
  
  .region-center-label {
    pointer-events: none;
    user-select: none;
  }
}

// 펄스 애니메이션
@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

// 연결선 펄스 애니메이션
@keyframes connectionPulse {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
}

// 선택된 연결선 펄스 애니메이션
@keyframes selectedPulse {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}


// 반응형 레이아웃 최적화
@media (max-width: 1200px) {
  .data-panel {
    width: 260px;
    padding: 16px;
    
    &.left-panel {
      left: 15px;
      bottom: 15px;
    }
    
    &.right-panel {
      right: 15px;
      top: 15px;
    }
  }
  
  .region-card {
    padding: 14px;
  }
  
  .card-header .region-name {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .map-container {
    flex-direction: column;
  }
  
  .data-panel {
    width: 100%;
    height: auto;
    max-height: 200px;
    padding: 10px;
    
    &.left-panel {
      border-right: none;
      border-bottom: 2px solid #4A90E2;
    }
    
    &.right-panel {
      border-left: none;
      border-top: 2px solid #4A90E2;
    }
  }
  
  .data-cards {
    flex-direction: row;
    overflow-x: auto;
    gap: 15px;
  }
  
  .region-card {
    min-width: 200px;
    padding: 10px;
  }
  
  .map-area {
    height: 60vh;
  }
}

@media (max-width: 480px) {
  .data-panel {
    padding: 8px;
    max-height: 150px;
  }
    
  .region-card {
    min-width: 160px;
      padding: 8px;
  }
      
  .card-header {
        margin-bottom: 8px;
        
    .region-name {
          font-size: 13px;
        }
        
    .equip-count .label {
            font-size: 11px;
          }
          
    .equip-count .count {
            font-size: 13px;
          }
        }
  
  .card-body .data-row {
    .label {
      font-size: 12px;
    }
    
    .value-group .value {
      font-size: 13px;
    }
    
    .value-group .unit {
      font-size: 11px;
    }
  }
}

// 지도 애니메이션 키프레임
@keyframes hoverScale {
  0% {
    transform: scale(1);
    filter: brightness(1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.2) drop-shadow(0 6px 12px rgba(26, 74, 138, 0.5));
  }
  100% {
    transform: scale(1.02);
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
}

@keyframes hoverGlow {
  0% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
  50% {
    filter: brightness(1.25) drop-shadow(0 6px 12px rgba(26, 74, 138, 0.5));
  }
  100% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
}


@keyframes pulse {
  0% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
  50% {
    filter: brightness(1.2) drop-shadow(0 6px 12px rgba(26, 74, 138, 0.4));
  }
  100% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
}

@keyframes glow {
  0% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
  50% {
    filter: brightness(1.3) drop-shadow(0 8px 16px rgba(26, 74, 138, 0.5));
  }
  100% {
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(26, 74, 138, 0.3));
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: scale(1.02);
  }
  40% {
    transform: scale(1.05);
  }
  60% {
    transform: scale(1.03);
  }
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.1);
    opacity: 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 5px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
  }
}


// 지도 path에 추가 애니메이션 효과
.map-path,
.sigungu-map .sigungu-path,
.dong-map .dong-path {
  // 선택된 상태에서 글로우 효과
  &.selected {
    animation: glow 2s ease-in-out infinite;
  }
  
  // 호버된 상태에서 펄스 효과 (카드에서 호버 시)
  &.hovered {
    animation: hoverGlow 1.5s ease-in-out infinite;
  }
}

// 읍면동 지도 전용 애니메이션 (더 섬세한 효과)
.dong-map .dong-path {
  &:hover {
    fill: #4A90E2 !important;
    stroke: #2E5BBA !important;
    stroke-width: 3 !important; // 읍면동은 더 얇은 테두리
    filter: brightness(1.1) drop-shadow(0 4px 8px rgba(46, 91, 186, 0.3)) !important;
  }
  
  &.hovered {
    animation: hoverGlow 2.5s ease-in-out infinite !important; // 더 느린 글로우
  }
}

// 시군구 지도 전용 애니메이션
.sigungu-map .sigungu-path {
  &:hover {
    fill: #4A90E2 !important;
    stroke: #2E5BBA !important;
    stroke-width: 3.5 !important; // 시군구는 중간 테두리
    filter: brightness(1.12) drop-shadow(0 5px 10px rgba(46, 91, 186, 0.35)) !important;
  }
  
  &.hovered {
    animation: hoverGlow 2.2s ease-in-out infinite !important; // 중간 속도 글로우
  }
}


</style>